import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useAccount, useDisconnect, useChainId, useSwitchChain } from 'wagmi';
import Exchange from './Exchange';
import DirectMessages from './DirectMessages';
import './GuildHall.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ARCO Token Configuration
const ARCO_TOKEN_ADDRESS = process.env.REACT_APP_ARCO_TOKEN_ADDRESS || '0x6D00EABF782Df498738f29e6558157d36518C663';
const POLYGON_CHAIN_ID = 137;
const POLYGON_RPC_URL = process.env.REACT_APP_POLYGON_RPC_URL || 'https://polygon-rpc.com';

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

function GuildHall({ user, token, onLogout, onUpdateUser, oathText, pendingAchievements, onAchievementsHandled }) {
  const navigate = useNavigate();
  
  // Web3Modal hooks
  const { open: openWeb3Modal } = useWeb3Modal();
  const { address: web3Address, isConnected: isWeb3Connected } = useAccount();
  const { disconnect: web3Disconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  
  // Refs for stable references in effects
  const userRef = useRef(user);
  const onUpdateUserRef = useRef(onUpdateUser);
  const tokenRef = useRef(token);
  
  // Keep refs updated
  useEffect(() => {
    userRef.current = user;
    onUpdateUserRef.current = onUpdateUser;
    tokenRef.current = token;
  }, [user, onUpdateUser, token]);
  
  const [walletAddress, setWalletAddress] = useState(user?.wallet_address || null);
  const [arcoBalance, setArcoBalance] = useState(null);
  const [isConnectingWallet, setIsConnectingWallet] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [currentChatRoom, setCurrentChatRoom] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [currentChannel, setCurrentChannel] = useState('general');
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [offlineMembers, setOfflineMembers] = useState([]);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [newChannelName, setNewChannelName] = useState('');
  const [channelSettingsError, setChannelSettingsError] = useState(null);
  
  // Commons main tab (chat vs forum)
  const [commonsMainTab, setCommonsMainTab] = useState('chat');
  
  // Forum state
  const [forumDiscussions, setForumDiscussions] = useState([]);
  const [forumCategories, setForumCategories] = useState([]);
  const [selectedForumCategory, setSelectedForumCategory] = useState('all');
  const [forumSearchQuery, setForumSearchQuery] = useState('');
  const [selectedForumDiscussion, setSelectedForumDiscussion] = useState(null);
  const [forumReplies, setForumReplies] = useState([]);
  const [newForumTitle, setNewForumTitle] = useState('');
  const [newForumContent, setNewForumContent] = useState('');
  const [newForumCategory, setNewForumCategory] = useState('general');
  const [newForumReply, setNewForumReply] = useState('');
  const [showNewForumPost, setShowNewForumPost] = useState(false);
  const [forumLoading, setForumLoading] = useState(false);
  const [showForumSettings, setShowForumSettings] = useState(false);
  const [forumSettingsTab, setForumSettingsTab] = useState('categories');
  const [forumHistory, setForumHistory] = useState([]);
  const [newForumCategoryName, setNewForumCategoryName] = useState('');
  const [newForumCategoryColor, setNewForumCategoryColor] = useState('#6b7280');
  
  // Chat channels configuration - now stored in state for dynamic updates
  const [chatChannels, setChatChannels] = useState([
    { id: 'general', name: 'general', description: 'Open discussion for all members' },
    { id: 'arcolia-updates', name: 'arcolia-updates', description: 'Official news and announcements' },
    { id: 'builders', name: 'builders', description: 'For creators, developers, dreamers' },
    { id: 'ideas', name: 'ideas', description: 'Share thoughts. Spark the future.' },
    { id: 'lore', name: 'lore', description: 'The stories of Arcolia' },
    { id: 'off-topic', name: 'off-topic', description: 'Talk about life beyond the chain' }
  ]);
  
  // Community guidelines
  const COMMUNITY_GUIDELINES = [
    "Be respectful. Build up, not down.",
    "This is a safe haven for all.",
    "No spam or self-promotion without permission.",
    "Keep discussions relevant to the channel.",
    "Protect the privacy of fellow members.",
    "Report issues to Founders or Aurels."
  ];
  const [allUsers, setAllUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminSuccess, setAdminSuccess] = useState(null);
  const [rooms, setRooms] = useState([]);
  
  // Exchange state
  const [showExchange, setShowExchange] = useState(false);
  
  // Direct Messages state
  const [showDirectMessages, setShowDirectMessages] = useState(false);
  const [unreadDMCount, setUnreadDMCount] = useState(0);
  
  // Mobile menu state
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Donation state
  const [donationAmount, setDonationAmount] = useState('');
  const [donationCurrency, setDonationCurrency] = useState('ARCO');
  const [donationTxHash, setDonationTxHash] = useState('');
  const [donationSuccess, setDonationSuccess] = useState(null);
  const [donationError, setDonationError] = useState(null);
  
  // Settings state
  const [guildSettings, setGuildSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [settingsSuccess, setSettingsSuccess] = useState(null);
  const [editingRoles, setEditingRoles] = useState({});
  const [editingRooms, setEditingRooms] = useState({});
  
  // Donations management state (for Founders)
  const [pendingDonations, setPendingDonations] = useState([]);
  const [verifiedDonations, setVerifiedDonations] = useState([]);
  const [allDonations, setAllDonations] = useState([]);
  const [donationsTab, setDonationsTab] = useState('pending');
  const [donationsLoading, setDonationsLoading] = useState(false);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [changingUsername, setChangingUsername] = useState(false);
  const [selectedAvatarInfo, setSelectedAvatarInfo] = useState(null);
  
  // Username history modal state (for Founders viewing other users)
  const [showUsernameHistory, setShowUsernameHistory] = useState(false);
  const [usernameHistoryUser, setUsernameHistoryUser] = useState(null);
  const [usernameHistoryData, setUsernameHistoryData] = useState(null);
  
  // Change password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Bulletin subscription state
  const [bulletinOptedIn, setBulletinOptedIn] = useState(false);
  const [bulletinLoading, setBulletinLoading] = useState(false);

  // Achievement state
  const [userAchievements, setUserAchievements] = useState([]);
  const [allAchievements, setAllAchievements] = useState([]);
  const [displayBadge, setDisplayBadge] = useState(null);
  const [userBadges, setUserBadges] = useState({});
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [achievementUsers, setAchievementUsers] = useState([]);
  const [awardingAchievement, setAwardingAchievement] = useState(false);
  const [selectedAwardUser, setSelectedAwardUser] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [achievementPopup, setAchievementPopup] = useState(null); // For showing achievement unlock notification
  const [pendingRewards, setPendingRewards] = useState({ total: 0, count: 0, achievements: [] });
  const [claimingRewards, setClaimingRewards] = useState(false);
  
  // Access denied popup state
  const [accessDeniedPopup, setAccessDeniedPopup] = useState(null);

  // The Journey state
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [journeyTasks, setJourneyTasks] = useState([]);
  const [journeyStats, setJourneyStats] = useState(null);
  const [journeySubmissions, setJourneySubmissions] = useState([]);
  const [pendingWitnessRequests, setPendingWitnessRequests] = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyTab, setJourneyTab] = useState('tasks'); // tasks, my-submissions, witness-requests, review (for Steward+)
  const [journeyReviewQueue, setJourneyReviewQueue] = useState([]);
  const [showJourneySubmitModal, setShowJourneySubmitModal] = useState(false);
  const [selectedJourneyTask, setSelectedJourneyTask] = useState(null);
  const [journeyProofDescription, setJourneyProofDescription] = useState('');
  const [journeyProofFile, setJourneyProofFile] = useState(null);
  const [journeyWitnesses, setJourneyWitnesses] = useState([]);
  const [journeySubmitting, setJourneySubmitting] = useState(false);
  const [journeyError, setJourneyError] = useState(null);
  const [journeySuccess, setJourneySuccess] = useState(null);
  const [showJourneyGiftModal, setShowJourneyGiftModal] = useState(false);
  const [journeyGiftUser, setJourneyGiftUser] = useState('');
  const [journeyGiftTask, setJourneyGiftTask] = useState('');
  const [journeyGiftNotes, setJourneyGiftNotes] = useState('');
  const [journeyPendingRewards, setJourneyPendingRewards] = useState({ total: 0, count: 0 });
  const [claimingJourneyRewards, setClaimingJourneyRewards] = useState(false);

  // Trial of Promotion state
  const [trialContent, setTrialContent] = useState(null);
  const [myTrial, setMyTrial] = useState(null);
  const [trialReviews, setTrialReviews] = useState({ active: [], passed: [], failed: [] });
  const [trialReviewTab, setTrialReviewTab] = useState('active'); // active, passed, failed
  const [pendingEndorsements, setPendingEndorsements] = useState([]);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState(null);
  const [trialSuccess, setTrialSuccess] = useState(null);
  const [showTrialStartConfirm, setShowTrialStartConfirm] = useState(false);
  const [selectedTrialForReview, setSelectedTrialForReview] = useState(null);
  // Stage submission state
  const [stage1Form, setStage1Form] = useState({
    contributed: '', learned: '', changed_mind: '', arcolia_purpose: '', why_responsibility: ''
  });
  const [stage2Files, setStage2Files] = useState([]);
  const [stage2Description, setStage2Description] = useState('');
  const [stage3Answers, setStage3Answers] = useState({
    scenario_1: '', scenario_2: '', scenario_3: '', scenario_4: '', scenario_5: ''
  });
  const [stage4SelectedEndorsers, setStage4SelectedEndorsers] = useState([]);
  const [stage5Choice, setStage5Choice] = useState('');
  const [endorsementProofText, setEndorsementProofText] = useState('');
  const [endorsementProofFile, setEndorsementProofFile] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [trialFinalFeedback, setTrialFinalFeedback] = useState('');
  const [trialFinalOutcome, setTrialFinalOutcome] = useState('');

  // Memoized sorted roles for performance
  const sortedRoleEntries = useMemo(() => {
    return Object.entries(editingRoles).sort((a, b) => a[1].order - b[1].order);
  }, [editingRoles]);

  const sortedRoleKeys = useMemo(() => {
    return Object.keys(editingRoles).sort((a, b) => editingRoles[a].order - editingRoles[b].order);
  }, [editingRoles]);

  // Treasury wallet
  const TREASURY_WALLET = "0xd858646D90cA89a987942509208b272983d53B65";
  const ACCEPTED_CURRENCIES = ["ARCO", "MATIC", "USDC", "USDT", "WETH", "WBTC"];

  // Room access requirements - roles and ARCO amounts
  const ROOM_ACCESS = {
    commons: { roles: [], arcoRequired: 0 },
    exchange: { roles: [], arcoRequired: 0 },
    sanctuary: { roles: ['Founder', 'Aurel', 'Archivist', 'Steward', 'Inner Circle', 'Honored Member'], arcoRequired: 0 },
    archives: { roles: ['Founder', 'Aurel', 'Archivist', 'Steward', 'Inner Circle', 'Honored Member'], arcoRequired: 0 },
    council: { roles: ['Founder', 'Aurel', 'Archivist', 'Steward'], arcoRequired: 250000 },
    treasury: { roles: ['Founder', 'Aurel', 'Archivist', 'Steward'], arcoRequired: 500000 }
  };

  // Room ARCO requirements (set to 0 - access controlled by roles only)
  const ROOM_REQUIREMENTS = {
    commons: 0,
    sanctuary: 0,
    treasury: 0,
    archives: 0,
    council: 0,
    exchange: 0
  };

  // Determine member status - use custom role if set, otherwise calculate from ARCO balance
  const getMemberStatus = () => {
    // If user has a custom role assigned, use that
    if (user?.role) {
      return user.role;
    }
    // Otherwise calculate from token balance
    if (arcoBalance === null || arcoBalance === undefined) return 'Member';
    if (arcoBalance >= 10000) return 'Aurel';
    if (arcoBalance >= 1000) return 'Archivist';
    if (arcoBalance >= 100) return 'Steward';
    if (arcoBalance >= 10) return 'Inner Circle';
    return 'Honored Member';
  };

  const isFounder = user?.role === 'Founder';

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications?unread_only=true&limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadDMCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [token]);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Heartbeat for online status - ping server every 30 seconds
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        await fetch(`${API_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    };
    
    // Send initial heartbeat
    sendHeartbeat();
    
    // Set up interval
    const heartbeatInterval = setInterval(sendHeartbeat, 30000); // Every 30 seconds
    
    // Cleanup on unmount
    return () => clearInterval(heartbeatInterval);
  }, [token]);

  // Check ARCO balance when wallet is connected
  const checkArcoBalance = useCallback(async () => {
    if (!walletAddress) return;
    try {
      const provider = new ethers.JsonRpcProvider(POLYGON_RPC_URL);
      const tokenContract = new ethers.Contract(ARCO_TOKEN_ADDRESS, ERC20_ABI, provider);
      
      const balance = await tokenContract.balanceOf(walletAddress);
      const decimals = await tokenContract.decimals();
      const formattedBalance = parseFloat(ethers.formatUnits(balance, decimals));
      
      setArcoBalance(formattedBalance);
    } catch (err) {
      console.error('Error checking ARCO balance:', err);
      setArcoBalance(0);
    }
    // Note: POLYGON_RPC_URL, ARCO_TOKEN_ADDRESS, ERC20_ABI are module-level constants, not dependencies
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress) {
      checkArcoBalance();
    }
  }, [walletAddress, checkArcoBalance]);

  // Sync Web3Modal connection with app state
  useEffect(() => {
    const syncWallet = async () => {
      if (isWeb3Connected && web3Address) {
        // Check if on correct chain
        if (chainId !== POLYGON_CHAIN_ID) {
          try {
            switchChain({ chainId: POLYGON_CHAIN_ID });
          } catch (err) {
            console.error('Failed to switch chain:', err);
            setWalletError('Please switch to Polygon network');
            return;
          }
        }
        
        // Only link if not already linked
        if (walletAddress?.toLowerCase() !== web3Address.toLowerCase()) {
          try {
            const response = await fetch(`${API_URL}/api/auth/link-wallet`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokenRef.current}`
              },
              body: JSON.stringify({ wallet_address: web3Address })
            });

            if (response.ok) {
              setWalletAddress(web3Address.toLowerCase());
              onUpdateUserRef.current({ ...userRef.current, wallet_address: web3Address.toLowerCase() });
              setShowWalletModal(false);
              setWalletError(null);
            } else {
              const data = await response.json();
              setWalletError(data.detail || 'Failed to link wallet');
            }
          } catch (err) {
            console.error('Wallet link error:', err);
            setWalletError(err.message || 'Failed to link wallet');
          }
        }
      }
    };
    
    syncWallet();
    // Using refs for user, token, onUpdateUser to avoid unnecessary re-runs
  }, [isWeb3Connected, web3Address, chainId, switchChain, walletAddress]);

  const connectWallet = async () => {
    setWalletError(null);
    try {
      await openWeb3Modal();
    } catch (err) {
      console.error('Wallet connection error:', err);
      setWalletError(err.message || 'Failed to connect wallet');
    }
  };

  const disconnectWallet = async () => {
    try {
      // Disconnect from Web3Modal
      web3Disconnect();
      
      // Unlink from account via API
      const response = await fetch(`${API_URL}/api/auth/unlink-wallet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setWalletAddress(null);
        setArcoBalance(null);
        onUpdateUser({ ...user, wallet_address: null });
      }
    } catch (err) {
      console.error('Error unlinking wallet:', err);
    }
  };

  // Admin functions
  const fetchAllUsers = async () => {
    setAdminLoading(true);
    setAdminError(null);
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fetch users');
      }
      const data = await response.json();
      setAllUsers(data.users);
    } catch (err) {
      setAdminError(err.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    setAdminError(null);
    setAdminSuccess(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/update-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId, role: newRole })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update role');
      }
      const data = await response.json();
      setAdminSuccess(data.message);
      // Refresh user list
      fetchAllUsers();
      // If updating own role, update local state
      if (userId === user.id) {
        onUpdateUser({ ...user, role: newRole });
      }
    } catch (err) {
      setAdminError(err.message);
    }
  };

  const banUser = async (userId) => {
    setAdminError(null);
    setAdminSuccess(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/ban-user?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to ban user');
      }
      const data = await response.json();
      setAdminSuccess(data.message);
      fetchAllUsers();
    } catch (err) {
      setAdminError(err.message);
    }
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${username}"? This action cannot be undone.`)) {
      return;
    }
    
    setAdminError(null);
    setAdminSuccess(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/delete-user?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      const data = await response.json();
      setAdminSuccess(data.message);
      fetchAllUsers();
    } catch (err) {
      setAdminError(err.message);
    }
  };

  const verifyUser = async (userId) => {
    setAdminError(null);
    setAdminSuccess(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/verify-user?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to verify user');
      }
      const data = await response.json();
      setAdminSuccess(data.message);
      fetchAllUsers();
    } catch (err) {
      setAdminError(err.message);
    }
  };

  const openAdminModal = () => {
    setShowAdminModal(true);
    fetchAllUsers();
  };

  // Chat functions
  const openChatRoom = async (roomId) => {
    // All verified users have access (no ARCO requirements)
    setCurrentChatRoom(roomId);
    setCurrentChannel('general');
    setShowChatModal(true);
    setChatLoading(true);
    
    // Fetch messages and online members
    await Promise.all([
      fetchMessages(roomId, 'general'),
      fetchOnlineMembers()
    ]);
  };

  // Navigate to separate pages (like Sanctuary)
  const navigateToRoom = (roomId, path) => {
    // All verified users have access (no ARCO requirements)
    navigate(path);
  };

  const fetchMessages = async (roomId, channel) => {
    setChatLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/messages/${roomId}/${channel}?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const fetchOnlineMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/members/online`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOnlineMembers(data.online_members || []);
        setOfflineMembers(data.offline_members || []);
      }
    } catch (err) {
      console.error('Error fetching online members:', err);
      setOnlineMembers([]);
      setOfflineMembers([]);
    }
  };

  // Open Exchange with access check
  const openExchange = () => {
    // All verified users have access (no ARCO requirements)
    setShowExchange(true);
  };

  const switchChannel = async (channelId) => {
    setCurrentChannel(channelId);
    if (currentChatRoom) {
      await fetchMessages(currentChatRoom, channelId);
    }
  };

  const closeChatRoom = () => {
    setShowChatModal(false);
    setCurrentChatRoom(null);
    setCurrentChannel('general');
    setChatMessages([]);
    setOnlineMembers([]);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentChatRoom) return;
    
    try {
      const response = await fetch(`${API_URL}/api/messages/${currentChatRoom}/${currentChannel}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, data.data]);
        setNewMessage('');
        
        // Check for achievement unlocks
        if (data.achievements_unlocked && data.achievements_unlocked.length > 0) {
          showAchievementNotification(data.achievements_unlocked);
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const truncateWallet = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Channel management functions (Founder only)
  const addChannel = () => {
    if (!newChannelName.trim()) {
      setChannelSettingsError('Please enter a channel name');
      return;
    }
    
    const channelId = newChannelName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    if (chatChannels.find(c => c.id === channelId)) {
      setChannelSettingsError('Channel already exists');
      return;
    }
    
    setChatChannels([...chatChannels, {
      id: channelId,
      name: newChannelName.toLowerCase().replace(/\s+/g, '-'),
      description: ''
    }]);
    setNewChannelName('');
    setChannelSettingsError(null);
    saveChannelsToServer([...chatChannels, { id: channelId, name: newChannelName.toLowerCase().replace(/\s+/g, '-'), description: '' }]);
  };

  const deleteChannel = (channelId) => {
    if (channelId === 'general') {
      setChannelSettingsError('Cannot delete the general channel');
      return;
    }
    
    if (!window.confirm(`Delete #${channelId}? All messages in this channel will be lost.`)) {
      return;
    }
    
    const updated = chatChannels.filter(c => c.id !== channelId);
    setChatChannels(updated);
    
    // Switch to general if current channel was deleted
    if (currentChannel === channelId) {
      setCurrentChannel('general');
    }
    
    saveChannelsToServer(updated);
  };

  const renameChannel = (channelId, newName) => {
    if (!newName.trim()) return;
    
    const cleanName = newName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const updated = chatChannels.map(c => 
      c.id === channelId ? { ...c, name: cleanName, id: cleanName } : c
    );
    setChatChannels(updated);
    setEditingChannel(null);
    saveChannelsToServer(updated);
  };

  const moveChannel = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= chatChannels.length) return;
    
    const updated = [...chatChannels];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setChatChannels(updated);
    saveChannelsToServer(updated);
  };

  const saveChannelsToServer = async (channels) => {
    try {
      await fetch(`${API_URL}/api/admin/channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ channels })
      });
    } catch (err) {
      console.error('Error saving channels:', err);
    }
  };

  const loadChannelsFromServer = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.channels && data.channels.length > 0) {
          setChatChannels(data.channels);
        }
      }
    } catch (err) {
      console.error('Error loading channels:', err);
    }
  }, [token]);

  // Load channels on mount
  useEffect(() => {
    loadChannelsFromServer();
  }, [loadChannelsFromServer]);

  // ==================== ACHIEVEMENT FUNCTIONS ====================

  // Fetch all achievements with user progress
  const fetchAchievements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/achievements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAchievements(data.achievements || []);
        setUserAchievements(data.achievements?.filter(a => a.earned) || []);
      }
    } catch (err) {
      console.error('Error fetching achievements:', err);
    }
  };

  // Fetch user badges for display
  const fetchUserBadges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/badges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserBadges(data.user_badges || {});
      }
    } catch (err) {
      console.error('Error fetching user badges:', err);
    }
  };

  // Fetch pending rewards
  const fetchPendingRewards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/achievements/pending-rewards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingRewards({
          total: data.total_pending_arco || 0,
          count: data.pending_count || 0,
          achievements: data.pending_achievements || [],
          hasWallet: data.has_wallet || false
        });
      }
    } catch (err) {
      console.error('Error fetching pending rewards:', err);
    }
  };

  // Claim pending rewards
  const claimPendingRewards = async () => {
    if (!isWeb3Connected) {
      setProfileError('Please connect your wallet first to claim rewards');
      return;
    }
    
    setClaimingRewards(true);
    setProfileError(null);
    setProfileSuccess(null);
    
    try {
      const response = await fetch(`${API_URL}/api/achievements/claim-rewards`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setProfileSuccess(`${data.message} Transaction: ${data.tx_hash?.slice(0, 10)}...`);
        // Refresh pending rewards and achievements
        fetchPendingRewards();
        fetchAchievements();
      } else {
        setProfileError(data.detail || 'Failed to claim rewards');
      }
    } catch (err) {
      console.error('Error claiming rewards:', err);
      setProfileError('Failed to claim rewards. Please try again.');
    } finally {
      setClaimingRewards(false);
    }
  };

  // Fetch current display badge
  const fetchDisplayBadge = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/display-badge`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDisplayBadge(data.display_badge);
      }
    } catch (err) {
      console.error('Error fetching display badge:', err);
    }
  };

  // Set display badge
  const setMyDisplayBadge = async (achievementId) => {
    try {
      const response = await fetch(`${API_URL}/api/profile/display-badge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ achievement_id: achievementId })
      });
      if (response.ok) {
        const data = await response.json();
        setDisplayBadge(data.display_badge);
        setProfileSuccess(achievementId ? 'Display badge set!' : 'Display badge cleared');
        setTimeout(() => setProfileSuccess(null), 2000);
        fetchUserBadges();
      }
    } catch (err) {
      setProfileError('Failed to set display badge');
    }
  };

  // ==================== THE JOURNEY FUNCTIONS ====================
  
  // Fetch journey tasks
  const fetchJourneyTasks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/journey/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJourneyTasks(data.tasks || []);
      }
    } catch (err) {
      console.error('Error fetching journey tasks:', err);
    }
  };

  // Fetch journey stats
  const fetchJourneyStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/journey/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJourneyStats(data);
        // Calculate pending rewards
        setJourneyPendingRewards({
          total: 0, // Will be shown when they claim
          count: data.pending_submissions || 0
        });
      }
    } catch (err) {
      console.error('Error fetching journey stats:', err);
    }
  };

  // Fetch user's journey submissions
  const fetchJourneySubmissions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/journey/my-submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJourneySubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error('Error fetching journey submissions:', err);
    }
  };

  // Fetch pending witness requests
  const fetchPendingWitnessRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/journey/pending-witnesses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingWitnessRequests(data.pending_requests || []);
      }
    } catch (err) {
      console.error('Error fetching witness requests:', err);
    }
  };

  // Fetch review queue (Steward+)
  const fetchJourneyReviewQueue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/journey/review-queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJourneyReviewQueue(data.submissions || []);
      }
    } catch (err) {
      console.error('Error fetching review queue:', err);
    }
  };

  // Submit journey proof
  const submitJourneyProof = async () => {
    if (!selectedJourneyTask || !journeyProofDescription.trim()) {
      setJourneyError('Please provide a description of your proof');
      return;
    }

    // Check witness requirements
    const task = journeyTasks.find(t => t.id === selectedJourneyTask.id);
    if (task && task.witnesses_required > 0 && journeyWitnesses.length < task.witnesses_required) {
      setJourneyError(`This task requires ${task.witnesses_required} witness(es). Please tag the required members.`);
      return;
    }

    setJourneySubmitting(true);
    setJourneyError(null);

    try {
      const formData = new FormData();
      formData.append('task_id', selectedJourneyTask.id);
      formData.append('proof_description', journeyProofDescription);
      formData.append('witness_user_ids', journeyWitnesses.join(','));
      
      if (journeyProofFile) {
        formData.append('proof_file', journeyProofFile);
      }

      const response = await fetch(`${API_URL}/api/journey/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setJourneySuccess('Submission received! ' + (data.witnesses_required > 0 ? 'Awaiting witness confirmations.' : 'Awaiting review.'));
        setShowJourneySubmitModal(false);
        setSelectedJourneyTask(null);
        setJourneyProofDescription('');
        setJourneyProofFile(null);
        setJourneyWitnesses([]);
        fetchJourneySubmissions();
        fetchJourneyStats();
        setTimeout(() => setJourneySuccess(null), 5000);
      } else {
        setJourneyError(data.detail || 'Failed to submit');
      }
    } catch (err) {
      console.error('Error submitting journey proof:', err);
      setJourneyError('Failed to submit. Please try again.');
    } finally {
      setJourneySubmitting(false);
    }
  };

  // Confirm as witness
  const confirmAsWitness = async (submissionId, confirm) => {
    try {
      const response = await fetch(`${API_URL}/api/journey/witness-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ submission_id: submissionId, confirm })
      });

      if (response.ok) {
        setJourneySuccess(confirm ? 'Confirmation recorded' : 'Declined');
        fetchPendingWitnessRequests();
        setTimeout(() => setJourneySuccess(null), 2000);
      }
    } catch (err) {
      setJourneyError('Failed to record confirmation');
    }
  };

  // Approve journey submission (Steward+)
  const approveJourneySubmission = async (submissionId, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/journey/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ submission_id: submissionId, notes })
      });

      const data = await response.json();

      if (response.ok) {
        setJourneySuccess(`Approved! ${data.reward_sent ? 'ARCO sent.' : 'ARCO pending (no wallet).'}`);
        fetchJourneyReviewQueue();
        setTimeout(() => setJourneySuccess(null), 3000);
      } else {
        setJourneyError(data.detail || 'Failed to approve');
      }
    } catch (err) {
      setJourneyError('Failed to approve submission');
    }
  };

  // Deny journey submission (Steward+)
  const denyJourneySubmission = async (submissionId, notes = '') => {
    const reason = notes || prompt('Reason for denial (optional):');
    
    try {
      const response = await fetch(`${API_URL}/api/journey/deny`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ submission_id: submissionId, notes: reason })
      });

      if (response.ok) {
        setJourneySuccess('Submission denied');
        fetchJourneyReviewQueue();
        setTimeout(() => setJourneySuccess(null), 2000);
      }
    } catch (err) {
      setJourneyError('Failed to deny submission');
    }
  };

  // Gift journey completion (Steward+)
  const giftJourneyCompletion = async () => {
    if (!journeyGiftUser || !journeyGiftTask) {
      setJourneyError('Please select a user and task');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/journey/gift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: journeyGiftUser,
          task_id: journeyGiftTask,
          notes: journeyGiftNotes
        })
      });

      const data = await response.json();

      if (response.ok) {
        setJourneySuccess(data.message);
        setShowJourneyGiftModal(false);
        setJourneyGiftUser('');
        setJourneyGiftTask('');
        setJourneyGiftNotes('');
        setTimeout(() => setJourneySuccess(null), 3000);
      } else {
        setJourneyError(data.detail || 'Failed to gift');
      }
    } catch (err) {
      setJourneyError('Failed to gift completion');
    }
  };

  // Claim pending journey rewards
  const claimJourneyRewards = async () => {
    if (!walletAddress) {
      setJourneyError('Please connect your wallet first');
      return;
    }

    setClaimingJourneyRewards(true);
    setJourneyError(null);

    try {
      const response = await fetch(`${API_URL}/api/journey/claim-pending`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        setJourneySuccess(`${data.message}`);
        fetchJourneyStats();
        fetchJourneySubmissions();
        setTimeout(() => setJourneySuccess(null), 3000);
      } else {
        setJourneyError(data.detail || 'Failed to claim');
      }
    } catch (err) {
      setJourneyError('Failed to claim rewards');
    } finally {
      setClaimingJourneyRewards(false);
    }
  };

  // ========== TRIAL OF PROMOTION FUNCTIONS ==========
  
  // Fetch trial content
  const fetchTrialContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrialContent(data);
      }
    } catch (err) {
      console.error('Error fetching trial content:', err);
    }
  };

  // Fetch my trial
  const fetchMyTrial = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/my-trial`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyTrial(data);
        // Pre-fill form if resuming a trial
        if (data.stage_1_reflections) {
          setStage1Form(data.stage_1_reflections);
        }
        if (data.stage_3_answers) {
          setStage3Answers(data.stage_3_answers);
        }
      }
    } catch (err) {
      console.error('Error fetching my trial:', err);
    }
  };

  // Fetch trial reviews (Steward+ only)
  const fetchTrialReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrialReviews(data);
      }
    } catch (err) {
      console.error('Error fetching trial reviews:', err);
    }
  };

  // Fetch pending endorsements
  const fetchPendingEndorsements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/pending-endorsements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingEndorsements(data.pending_endorsements || []);
      }
    } catch (err) {
      console.error('Error fetching pending endorsements:', err);
    }
  };

  // Start trial
  const startTrial = async () => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        setShowTrialStartConfirm(false);
        fetchMyTrial();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to start trial');
      }
    } catch (err) {
      setTrialError('Failed to start trial');
    } finally {
      setTrialLoading(false);
    }
  };

  // Submit Stage 1
  const submitStage1 = async () => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/stage1/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(stage1Form)
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        fetchMyTrial();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to submit');
      }
    } catch (err) {
      setTrialError('Failed to submit Stage 1');
    } finally {
      setTrialLoading(false);
    }
  };

  // Submit Stage 2
  const submitStage2 = async () => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/stage2/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ files: stage2Files, description: stage2Description })
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        fetchMyTrial();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to submit');
      }
    } catch (err) {
      setTrialError('Failed to submit Stage 2');
    } finally {
      setTrialLoading(false);
    }
  };

  // Submit Stage 3
  const submitStage3 = async () => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/stage3/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(stage3Answers)
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        fetchMyTrial();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to submit');
      }
    } catch (err) {
      setTrialError('Failed to submit Stage 3');
    } finally {
      setTrialLoading(false);
    }
  };

  // Select endorsers for Stage 4
  const selectEndorsers = async () => {
    if (stage4SelectedEndorsers.length < 2) {
      setTrialError('Please select at least 2 endorsers');
      return;
    }
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/stage4/select-endorsers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ endorser_ids: stage4SelectedEndorsers })
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        fetchMyTrial();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to select endorsers');
      }
    } catch (err) {
      setTrialError('Failed to select endorsers');
    } finally {
      setTrialLoading(false);
    }
  };

  // Submit endorsement (as an endorser for someone else's trial)
  const submitEndorsement = async (trialId) => {
    if (!endorsementProofText && !endorsementProofFile) {
      setTrialError('Please provide written proof or upload a file');
      return;
    }
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/stage4/submit-endorsement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trial_id: trialId,
          proof_text: endorsementProofText,
          proof_file: endorsementProofFile
        })
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        setEndorsementProofText('');
        setEndorsementProofFile(null);
        fetchPendingEndorsements();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to submit endorsement');
      }
    } catch (err) {
      setTrialError('Failed to submit endorsement');
    } finally {
      setTrialLoading(false);
    }
  };

  // Choose Stage 5 method
  const chooseStage5Method = async (choice) => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/stage5/choose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ choice })
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        fetchMyTrial();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to submit choice');
      }
    } catch (err) {
      setTrialError('Failed to submit choice');
    } finally {
      setTrialLoading(false);
    }
  };

  // Review trial stage (Steward+ only)
  const reviewTrialStage = async (trialId, stage, approved) => {
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/review/stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trial_id: trialId,
          stage: stage,
          approved: approved,
          notes: reviewNotes
        })
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        setReviewNotes('');
        setSelectedTrialForReview(null);
        fetchTrialReviews();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to review');
      }
    } catch (err) {
      setTrialError('Failed to review stage');
    } finally {
      setTrialLoading(false);
    }
  };

  // Complete trial (Steward+ only)
  const completeTrial = async (trialId) => {
    if (!trialFinalOutcome || !trialFinalFeedback) {
      setTrialError('Please select an outcome and provide feedback');
      return;
    }
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch(`${API_URL}/api/trial/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trial_id: trialId,
          outcome: trialFinalOutcome,
          feedback: trialFinalFeedback
        })
      });
      const data = await response.json();
      if (response.ok) {
        setTrialSuccess(data.message);
        setTrialFinalOutcome('');
        setTrialFinalFeedback('');
        setSelectedTrialForReview(null);
        fetchTrialReviews();
        setTimeout(() => setTrialSuccess(null), 3000);
      } else {
        setTrialError(data.detail || 'Failed to complete trial');
      }
    } catch (err) {
      setTrialError('Failed to complete trial');
    } finally {
      setTrialLoading(false);
    }
  };

  // Load journey data when modal opens
  useEffect(() => {
    if (showJourneyModal) {
      setJourneyLoading(true);
      Promise.all([
        fetchJourneyTasks(),
        fetchJourneyStats(),
        fetchJourneySubmissions(),
        fetchPendingWitnessRequests(),
        ['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role) ? fetchJourneyReviewQueue() : Promise.resolve()
      ]).finally(() => setJourneyLoading(false));
    }
  }, [showJourneyModal]);

  // Load trial data when journey modal opens or tab changes to trial
  useEffect(() => {
    if (showJourneyModal && (journeyTab === 'trial' || journeyTab === 'trial-reviews')) {
      fetchTrialContent();
      fetchMyTrial();
      fetchPendingEndorsements();
      if (['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role)) {
        fetchTrialReviews();
      }
    }
  }, [showJourneyModal, journeyTab]);

  // ==================== END JOURNEY FUNCTIONS ====================

  // Fetch achievement users for admin
  const fetchAchievementUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/achievements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAchievementUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching achievement users:', err);
    }
  };

  // Award achievement to user
  const awardAchievement = async (userId, achievementId, reason = '') => {
    setAwardingAchievement(true);
    try {
      const response = await fetch(`${API_URL}/api/achievements/award`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: userId,
          achievement_id: achievementId,
          reason: reason
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setProfileSuccess(`Achievement awarded! ${data.reward_sent ? 'ARCO sent!' : 'No wallet connected.'}`);
        setTimeout(() => setProfileSuccess(null), 3000);
        fetchAchievementUsers();
        return true;
      } else {
        setProfileError(data.detail || 'Failed to award achievement');
        return false;
      }
    } catch (err) {
      setProfileError('Failed to award achievement');
      return false;
    } finally {
      setAwardingAchievement(false);
    }
  };

  // Check if user can award achievements (Steward+)
  const canAwardAchievements = () => {
    const allowedRoles = ['Founder', 'Aurel', 'Archivist', 'Steward'];
    return allowedRoles.includes(user?.role);
  };

  // Load achievements when profile modal opens
  useEffect(() => {
    if (showProfileModal) {
      fetchAchievements();
      fetchDisplayBadge();
      fetchPendingRewards();
    }
  }, [showProfileModal]);

  // Load user badges on mount
  useEffect(() => {
    fetchUserBadges();
  }, []);

  // Handle pending achievements from oath acceptance
  useEffect(() => {
    if (pendingAchievements && pendingAchievements.length > 0) {
      showAchievementNotification(pendingAchievements);
      if (onAchievementsHandled) {
        onAchievementsHandled();  // Clear pending achievements in parent
      }
    }
  }, [pendingAchievements]);

  // Show achievement unlock notification
  const showAchievementNotification = (achievements) => {
    if (!achievements || achievements.length === 0) return;
    
    // Show first achievement (can queue multiple if needed)
    const achievement = achievements[0];
    
    // Use data directly from backend response (includes badge_url and reward_arco)
    setAchievementPopup({
      name: achievement.achievement,
      reward_sent: achievement.reward_sent,
      badge_url: achievement.badge_url,  // Now included in backend response
      reward_arco: achievement.reward_arco || 0  // Now included in backend response
    });
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setAchievementPopup(null);
    }, 5000);
    
    // Refresh achievements list
    fetchAchievements();
  };

  // ==================== FORUM FUNCTIONS ====================
  
  // Check if user can create forum posts (Inner Circle+)
  const canCreateForumPost = () => {
    const allowedRoles = ['Founder', 'Aurel', 'Archivist', 'Steward', 'Inner Circle'];
    return allowedRoles.includes(user?.role);
  };

  // Fetch forum categories
  const fetchForumCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching forum categories:', err);
    }
  };

  // Fetch forum discussions
  const fetchForumDiscussions = async (search = '', category = 'all') => {
    try {
      setForumLoading(true);
      let url = `${API_URL}/api/commons/forum/discussions`;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category && category !== 'all') params.append('category', category);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumDiscussions(data.discussions || []);
      }
    } catch (err) {
      console.error('Error fetching forum discussions:', err);
    } finally {
      setForumLoading(false);
    }
  };

  // Fetch replies for a discussion
  const fetchForumReplies = async (discussionId) => {
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/${discussionId}/replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumReplies(data.replies || []);
      }
    } catch (err) {
      console.error('Error fetching forum replies:', err);
    }
  };

  // Create a new forum discussion
  const createForumDiscussion = async () => {
    if (!newForumTitle.trim() || !newForumContent.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newForumTitle,
          content: newForumContent,
          category: newForumCategory
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewForumTitle('');
        setNewForumContent('');
        setNewForumCategory('general');
        setShowNewForumPost(false);
        fetchForumDiscussions(forumSearchQuery, selectedForumCategory);
        
        // Check for achievement unlocks
        if (data.achievements_unlocked && data.achievements_unlocked.length > 0) {
          showAchievementNotification(data.achievements_unlocked);
        }
      }
    } catch (err) {
      console.error('Error creating forum discussion:', err);
    }
  };

  // Add reply to discussion
  const addForumReply = async () => {
    if (!newForumReply.trim() || !selectedForumDiscussion) return;
    
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/${selectedForumDiscussion.id}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newForumReply })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewForumReply('');
        fetchForumReplies(selectedForumDiscussion.id);
        
        // Check for achievement unlocks
        if (data.achievements_unlocked && data.achievements_unlocked.length > 0) {
          showAchievementNotification(data.achievements_unlocked);
        }
      }
    } catch (err) {
      console.error('Error adding forum reply:', err);
    }
  };

  // Pin/unpin discussion
  const togglePinForumDiscussion = async (discussionId) => {
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/${discussionId}/pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchForumDiscussions(forumSearchQuery, selectedForumCategory);
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
    }
  };

  // Delete discussion (soft)
  const deleteForumDiscussion = async (discussionId) => {
    if (!window.confirm('Move this discussion to history?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/${discussionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchForumDiscussions(forumSearchQuery, selectedForumCategory);
        if (selectedForumDiscussion?.id === discussionId) {
          setSelectedForumDiscussion(null);
        }
      }
    } catch (err) {
      console.error('Error deleting discussion:', err);
    }
  };

  // Fetch forum history
  const fetchForumHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumHistory(data.discussions || []);
      }
    } catch (err) {
      console.error('Error fetching forum history:', err);
    }
  };

  // Restore discussion from history
  const restoreForumDiscussion = async (discussionId) => {
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/${discussionId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchForumHistory();
        fetchForumDiscussions(forumSearchQuery, selectedForumCategory);
      }
    } catch (err) {
      console.error('Error restoring discussion:', err);
    }
  };

  // Permanently delete discussion
  const permanentDeleteForumDiscussion = async (discussionId) => {
    if (!window.confirm('Permanently delete this discussion? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/discussions/${discussionId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchForumHistory();
      }
    } catch (err) {
      console.error('Error permanently deleting discussion:', err);
    }
  };

  // Save forum categories
  const saveForumCategories = async (categories) => {
    try {
      const response = await fetch(`${API_URL}/api/commons/forum/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categories })
      });
      if (response.ok) {
        setForumCategories(categories);
      }
    } catch (err) {
      console.error('Error saving forum categories:', err);
    }
  };

  // Add forum category
  const addForumCategory = () => {
    if (!newForumCategoryName.trim()) return;
    
    const categoryId = newForumCategoryName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (forumCategories.find(c => c.id === categoryId)) return;
    
    const newCategories = [...forumCategories, {
      id: categoryId,
      name: newForumCategoryName.trim(),
      color: newForumCategoryColor
    }];
    
    saveForumCategories(newCategories);
    setNewForumCategoryName('');
    setNewForumCategoryColor('#6b7280');
  };

  // Remove forum category
  const removeForumCategory = (categoryId) => {
    if (categoryId === 'general') return;
    const newCategories = forumCategories.filter(c => c.id !== categoryId);
    saveForumCategories(newCategories);
  };

  // Get category info helper
  const getForumCategoryInfo = (categoryId) => {
    return forumCategories.find(c => c.id === categoryId) || { id: categoryId, name: categoryId, color: '#6b7280' };
  };

  // Load forum data when tab changes to forum
  useEffect(() => {
    if (commonsMainTab === 'forum' && showChatModal) {
      fetchForumCategories();
      fetchForumDiscussions();
    }
  }, [commonsMainTab, showChatModal]);

  // Load forum history when settings tab changes
  useEffect(() => {
    if (showForumSettings && forumSettingsTab === 'history') {
      fetchForumHistory();
    }
  }, [showForumSettings, forumSettingsTab]);

  // Format date for forum
  const formatForumDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Donation functions
  const submitDonation = async () => {
    if (!donationAmount || !donationCurrency) {
      setDonationError('Please enter amount and select currency');
      return;
    }
    
    setDonationError(null);
    setDonationSuccess(null);
    
    try {
      const response = await fetch(`${API_URL}/api/donations/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: donationAmount,
          currency: donationCurrency,
          tx_hash: donationTxHash
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDonationSuccess(data.message);
        setDonationAmount('');
        setDonationTxHash('');
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to record donation');
      }
    } catch (err) {
      setDonationError(err.message);
    }
  };

  // Check room access (now role-based only, no ARCO requirements)
  const canAccessRoom = (roomId) => {
    const access = ROOM_ACCESS[roomId];
    if (!access) return true;
    
    // No restrictions for this room
    if (access.roles.length === 0 && access.arcoRequired === 0) return true;
    
    const userRole = user?.role || 'Member';
    const userArco = arcoBalance || 0;
    
    // Founders always have access to everything
    if (userRole === 'Founder') return true;
    
    // Check role requirement
    const hasRoleAccess = access.roles.length === 0 || access.roles.includes(userRole);
    
    // Check ARCO requirement
    const hasArcoAccess = userArco >= access.arcoRequired;
    
    return hasRoleAccess && hasArcoAccess;
  };
  
  const getAccessRequirements = (roomId) => {
    const access = ROOM_ACCESS[roomId];
    if (!access) return null;
    
    const roomNames = {
      commons: 'The Commons',
      exchange: 'The Arcolian Exchange',
      sanctuary: 'The Sanctuary',
      archives: 'The Archives',
      council: 'Council Chamber',
      treasury: 'The Treasury'
    };
    
    return {
      roomName: roomNames[roomId] || roomId,
      roles: access.roles,
      arcoRequired: access.arcoRequired,
      minRole: access.roles.length > 0 ? access.roles[access.roles.length - 1] : null
    };
  };
  
  const handleRoomClick = (roomId) => {
    if (canAccessRoom(roomId)) {
      // Handle navigation based on room
      if (roomId === 'treasury') {
        navigate('/treasury');
      } else if (roomId === 'council') {
        navigate('/council');
      } else if (roomId === 'archives') {
        navigate('/archives');
      } else if (roomId === 'sanctuary') {
        navigate('/sanctuary');
      } else if (roomId === 'exchange') {
        setShowExchange(true);
      } else if (roomId === 'commons') {
        openChatRoom(roomId);
      } else {
        openChatRoom(roomId);
      }
    } else {
      // Show access denied popup
      const requirements = getAccessRequirements(roomId);
      setAccessDeniedPopup(requirements);
    }
  };

  const formatArcoRequirement = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(0)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount.toString();
  };

  // Settings functions
  const fetchSettings = async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fetch settings');
      }
      const data = await response.json();
      setGuildSettings(data);
      setEditingRoles(JSON.parse(JSON.stringify(data.roles || {})));
      setEditingRooms(JSON.parse(JSON.stringify(data.rooms || {})));
    } catch (err) {
      setSettingsError(err.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    setSettingsError(null);
    setSettingsSuccess(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ roles: editingRoles, rooms: editingRooms })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }
      const data = await response.json();
      setSettingsSuccess('Settings saved successfully!');
      setGuildSettings(data.settings);
    } catch (err) {
      setSettingsError(err.message);
    }
  };

  const updateRoleDisplayName = (roleKey, newName) => {
    setEditingRoles(prev => ({
      ...prev,
      [roleKey]: { ...prev[roleKey], displayName: newName }
    }));
  };

  const toggleRoomAccess = (roomKey, roleKey) => {
    setEditingRooms(prev => {
      const room = prev[roomKey];
      const currentRoles = room.allowedRoles || [];
      const newRoles = currentRoles.includes(roleKey)
        ? currentRoles.filter(r => r !== roleKey)
        : [...currentRoles, roleKey];
      return {
        ...prev,
        [roomKey]: { ...room, allowedRoles: newRoles }
      };
    });
  };

  const openSettingsModal = () => {
    setShowSettingsModal(true);
    fetchSettings();
    fetchDonations();
  };

  // Fetch donations for management (Founders only)
  const fetchDonations = async () => {
    if (!isFounder) return;
    
    setDonationsLoading(true);
    try {
      // Fetch all donations
      const response = await fetch(`${API_URL}/api/donations/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const donations = data.donations || [];
        
        // Separate by status
        setPendingDonations(donations.filter(d => d.status === 'pending'));
        setVerifiedDonations(donations.filter(d => d.status === 'verified'));
        setAllDonations(donations);
      }
    } catch (err) {
      console.error('Error fetching donations:', err);
    } finally {
      setDonationsLoading(false);
    }
  };

  // Verify or reject a donation
  const handleDonationAction = async (donationId, status, newRole = null) => {
    try {
      const response = await fetch(`${API_URL}/api/donations/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ donation_id: donationId, status, new_role: newRole })
      });
      
      if (response.ok) {
        // Refresh donations list
        fetchDonations();
        setSettingsSuccess(`Donation ${status} successfully!`);
        setTimeout(() => setSettingsSuccess(null), 3000);
      } else {
        const data = await response.json();
        setSettingsError(data.detail || 'Failed to update donation');
      }
    } catch (err) {
      setSettingsError('Error updating donation');
    }
  };

  // Profile functions
  const openProfileModal = async () => {
    setShowProfileModal(true);
    setProfileLoading(true);
    setProfileError(null);
    setNewUsername('');
    setUsernameAvailable(null);
    setSelectedAvatarInfo(null);
    
    try {
      // Fetch profile data
      const profileRes = await fetch(`${API_URL}/api/profile/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfileData(data);
      }
      
      // Fetch bulletin subscription status
      const bulletinRes = await fetch(`${API_URL}/api/bulletin/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bulletinRes.ok) {
        const bulletinData = await bulletinRes.json();
        setBulletinOptedIn(bulletinData.opted_in || false);
      }
      
      // Fetch available avatars
      const avatarsRes = await fetch(`${API_URL}/api/profile/avatars`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (avatarsRes.ok) {
        const data = await avatarsRes.json();
        setAvailableAvatars(data.avatars || []);
        // Set initial selected avatar info if user has one
        if (data.current_avatar_id) {
          const currentAvatar = data.avatars?.find(a => a.id === data.current_avatar_id);
          if (currentAvatar) {
            setSelectedAvatarInfo(currentAvatar);
          }
        }
      }
    } catch (err) {
      setProfileError('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const selectAvatar = async (avatarId) => {
    // Find the avatar in the available list
    const avatar = availableAvatars.find(a => a.id === avatarId);
    if (avatar) {
      setSelectedAvatarInfo(avatar);
    }
    
    try {
      const response = await fetch(`${API_URL}/api/profile/avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ avatar_id: avatarId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfileData(prev => ({ ...prev, avatar_id: data.avatar_id, avatar_url: data.avatar_url }));
        // Update user object
        if (onUpdateUser) {
          onUpdateUser({ ...user, avatar_id: data.avatar_id, avatar_url: data.avatar_url });
        }
        setProfileSuccess('Avatar updated!');
        setTimeout(() => setProfileSuccess(null), 2000);
      } else {
        const err = await response.json();
        setProfileError(err.detail || 'Failed to update avatar');
      }
    } catch (err) {
      setProfileError('Failed to update avatar');
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    
    setCheckingUsername(true);
    try {
      const response = await fetch(`${API_URL}/api/profile/username/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ new_username: username })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsernameAvailable(data.available ? data : { available: false, reason: data.reason });
      }
    } catch (err) {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  // Debounced username check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newUsername && newUsername !== profileData?.username) {
        checkUsernameAvailability(newUsername);
      } else {
        setUsernameAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newUsername, profileData?.username]);

  const handleUsernameChange = async () => {
    if (!usernameAvailable?.available) return;
    
    setChangingUsername(true);
    setProfileError(null);
    
    try {
      // Check if this is a free change or paid
      if (profileData?.next_change_free) {
        // Free change
        const response = await fetch(`${API_URL}/api/profile/username/change-free`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ new_username: newUsername })
        });
        
        if (response.ok) {
          const data = await response.json();
          setProfileData(prev => ({
            ...prev,
            username: data.new_username,
            changes_remaining: prev.changes_remaining - 1,
            next_change_free: false
          }));
          if (onUpdateUser) {
            onUpdateUser({ ...user, username: data.new_username });
          }
          setProfileSuccess('Username changed successfully!');
          setNewUsername('');
          setUsernameAvailable(null);
          setTimeout(() => setProfileSuccess(null), 3000);
        } else {
          const err = await response.json();
          setProfileError(err.detail || 'Failed to change username');
        }
      } else {
        // Paid change - create checkout
        const response = await fetch(`${API_URL}/api/profile/username/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ new_username: newUsername })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          const err = await response.json();
          setProfileError(err.detail || 'Failed to start checkout');
        }
      }
    } catch (err) {
      setProfileError('Failed to change username');
    } finally {
      setChangingUsername(false);
    }
  };

  // Check for username change completion on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const usernameChangeStatus = params.get('username_change');
    const sessionId = params.get('session_id');
    
    if (usernameChangeStatus === 'success' && sessionId) {
      // Verify the payment and apply username change
      const verifyUsernameChange = async () => {
        try {
          const response = await fetch(`${API_URL}/api/profile/username/checkout-status/${sessionId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success' && data.username) {
              if (onUpdateUser) {
                onUpdateUser({ ...user, username: data.username });
              }
              setProfileSuccess(`Username changed to ${data.username}!`);
            }
          }
        } catch (err) {
          console.error('Error verifying username change:', err);
        }
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
      };
      verifyUsernameChange();
    }
  }, [token]);

  // Fetch username history for a user (Founder only)
  const fetchUsernameHistory = async (userId, username) => {
    setUsernameHistoryUser({ user_id: userId, username });
    setShowUsernameHistory(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/user/${userId}/username-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsernameHistoryData(data);
      }
    } catch (err) {
      console.error('Error fetching username history:', err);
    }
  };

  // Change password function
  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordForm(false);
        setTimeout(() => setPasswordSuccess(null), 3000);
      } else {
        const err = await response.json();
        setPasswordError(err.detail || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Bulletin subscription toggle
  const handleBulletinToggle = async () => {
    setBulletinLoading(true);
    const endpoint = bulletinOptedIn ? '/api/bulletin/opt-out' : '/api/bulletin/opt-in';
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setBulletinOptedIn(!bulletinOptedIn);
      }
    } catch (err) {
      console.error('Failed to update bulletin subscription:', err);
    } finally {
      setBulletinLoading(false);
    }
  };

  const handleNavigation = (area) => {
    if (!walletAddress) {
      setShowWalletModal(true);
      return;
    }
    
    if (arcoBalance === null || arcoBalance < 1) {
      setWalletError('You need at least 1 ARCO token to access this area');
      setShowWalletModal(true);
      return;
    }

    // For now, show alert for areas under development
    if (area === 'sanctuary') {
      navigate('/sanctuary');
    } else if (area === 'treasury') {
      // Navigate to Treasury page
      navigate('/treasury');
    }
  };

  const handleComingSoon = (area) => {
    alert('This area is locked. Coming soon!');
  };

  return (
    <div className="guild-hall" data-testid="guild-hall">
      {/* Background */}
      <div className="guild-background">
        <img 
          src={process.env.REACT_APP_GUILD_HALL_IMAGE_URL}
          alt="Guild Hall"
          className="guild-bg-image"
        />
        <div className="guild-overlay"></div>
      </div>

      {/* Header */}
      <header className="guild-header">
        {/* Admin Controls - Left Side (Desktop) */}
        <div className="founder-controls desktop-only">
          {isFounder && (
            <>
              <button className="admin-btn settings-btn" onClick={openSettingsModal} data-testid="settings-btn">
                🛡️ Guild Settings
              </button>
              <button className="admin-btn" onClick={openAdminModal} data-testid="admin-btn">
                👥 Manage Members
              </button>
            </>
          )}
          {canAwardAchievements() && (
            <button className="admin-btn" onClick={() => { setShowAchievementModal(true); fetchAchievementUsers(); fetchAchievements(); }} data-testid="achievement-btn">
              🏆 Award Achievement
            </button>
          )}
        </div>
        
        <h1 className="guild-title" data-testid="guild-title">The Guild Hall</h1>
        
        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          data-testid="mobile-menu-toggle"
        >
          {showMobileMenu ? '✕' : '☰'}
        </button>
        
        {/* General Controls - Right Side (Desktop) */}
        <div className="header-buttons desktop-only">
          <button 
            className="header-btn vault-btn"
            onClick={() => navigate('/vault')}
            data-testid="vault-btn"
          >
            🏛️ Arcolian Vault
          </button>
          <button className="header-btn rules-btn" onClick={() => setShowRulesModal(true)} data-testid="rules-btn">
            📜 Code & Oath
          </button>
          <button className="header-btn donate-btn" onClick={() => setShowDonateModal(true)} data-testid="donate-btn">
            💎 Support Arcolia
          </button>
          <button className="logout-btn" onClick={onLogout} data-testid="logout-btn">
            Leave Guild
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="mobile-menu-dropdown" data-testid="mobile-menu">
            {isFounder && (
              <>
                <button className="mobile-menu-item" onClick={() => { openSettingsModal(); setShowMobileMenu(false); }}>
                  🛡️ Guild Settings
                </button>
                <button className="mobile-menu-item" onClick={() => { openAdminModal(); setShowMobileMenu(false); }}>
                  👥 Manage Members
                </button>
              </>
            )}
            {canAwardAchievements() && (
              <button className="mobile-menu-item" onClick={() => { setShowAchievementModal(true); fetchAchievementUsers(); fetchAchievements(); setShowMobileMenu(false); }}>
                🏆 Award Achievement
              </button>
            )}
            {(isFounder || canAwardAchievements()) && <div className="mobile-menu-divider"></div>}
            <button className="mobile-menu-item" onClick={() => { navigate('/vault'); setShowMobileMenu(false); }}>
              🏛️ Arcolian Vault
            </button>
            <button className="mobile-menu-item" onClick={() => { setShowRulesModal(true); setShowMobileMenu(false); }}>
              📜 Code & Oath
            </button>
            <button className="mobile-menu-item" onClick={() => { setShowDonateModal(true); setShowMobileMenu(false); }}>
              💎 Support Arcolia
            </button>
            <div className="mobile-menu-divider"></div>
            <button className="mobile-menu-item logout" onClick={onLogout}>
              🚪 Leave Guild
            </button>
          </div>
        )}
      </header>

      {/* The Commons & Exchange - Side by side */}
      <div className="commons-exchange-trigger">
        <button 
          className={`commons-btn ${canAccessRoom('commons') ? 'accessible' : 'locked'}`}
          onClick={() => handleRoomClick('commons')}
          data-testid="commons-btn"
        >
          💬 The Commons
        </button>
        <button 
          className="dm-trigger-btn"
          onClick={() => setShowDirectMessages(true)}
          data-testid="dm-btn"
        >
          ✉️ Messages
          {unreadDMCount > 0 && <span className="dm-badge">{unreadDMCount}</span>}
        </button>
        <button 
          className={`exchange-btn ${canAccessRoom('exchange') ? 'accessible' : 'locked'}`}
          onClick={() => handleRoomClick('exchange')}
          data-testid="exchange-btn"
        >
          {canAccessRoom('exchange') ? '📜' : '🔒'} The Arcolian Exchange
        </button>
      </div>

      {/* Coming Soon Areas - Top */}
      <div className="coming-soon-areas">
        <button 
          className={`coming-soon-btn left ${canAccessRoom('archives') ? 'accessible' : ''}`}
          onClick={() => handleRoomClick('archives')}
          data-testid="nav-archives"
        >
          <span className="lock-icon">{canAccessRoom('archives') ? '📚' : '🔒'}</span>
          <span className="area-name">Archives</span>
        </button>
        
        <button 
          className={`coming-soon-btn right ${canAccessRoom('council') ? 'accessible' : ''}`}
          onClick={() => handleRoomClick('council')}
          data-testid="nav-council"
        >
          <span className="lock-icon">{canAccessRoom('council') ? '👑' : '🔒'}</span>
          <span className="area-name">Council Chamber</span>
        </button>
      </div>

      {/* Center Member Info */}
      <div className="member-info-center" data-testid="member-info">
        <div className="member-card clickable" onClick={openProfileModal} data-testid="open-profile-btn">
          {/* Profile Avatar */}
          <div className="member-avatar-display">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="Avatar" className="member-avatar-image" />
            ) : (
              <div className="member-avatar-placeholder">
                {user?.username?.charAt(0).toUpperCase() || '?'}
              </div>
            )}
            <div className="edit-profile-hint">Edit Profile</div>
          </div>
          <div className="member-status" data-testid="member-status">
            <span className="status-badge">{getMemberStatus()}</span>
          </div>
          <div className="member-username" data-testid="member-username">
            {user?.username || 'Unknown'}
          </div>
          
          {walletAddress ? (
            <div className="wallet-linked" onClick={(e) => e.stopPropagation()}>
              <div className="wallet-address-display" data-testid="wallet-address">
                <span className="wallet-icon">🔗</span>
                <span className="wallet-short">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
              </div>
              <div className="token-balance" data-testid="token-balance">
                <span className="token-icon">◈</span>
                <span className="balance-amount">
                  {arcoBalance !== null ? arcoBalance.toLocaleString() : '...'}
                </span>
                <span className="token-name">ARCO</span>
              </div>
              <button 
                className="wallet-action-btn unlink"
                onClick={disconnectWallet}
                data-testid="unlink-wallet-btn"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button 
              className="wallet-action-btn connect"
              onClick={(e) => { e.stopPropagation(); connectWallet(); }}
              data-testid="connect-wallet-btn"
            >
              🔗 Connect
            </button>
          )}
        </div>
        
        {/* The Journey Button */}
        <button 
          className="journey-btn"
          onClick={() => setShowJourneyModal(true)}
          data-testid="journey-btn"
        >
          <span className="journey-icon">✦</span>
          <span className="journey-text">The Journey</span>
          {pendingWitnessRequests.length > 0 && (
            <span className="journey-badge">{pendingWitnessRequests.length}</span>
          )}
        </button>
      </div>

      {/* Navigation Areas - Bottom */}
      <div className="navigation-areas">
        <button 
          className={`nav-area-btn sanctuary ${canAccessRoom('sanctuary') ? 'accessible' : 'locked'}`}
          onClick={() => handleRoomClick('sanctuary')}
          data-testid="nav-sanctuary"
          title="The Sanctuary"
        >
          <span className="nav-icon">{canAccessRoom('sanctuary') ? '🕯️' : '🔒'}</span>
          <span className="nav-name">The Sanctuary</span>
        </button>

        <button 
          className={`nav-area-btn treasury ${canAccessRoom('treasury') ? 'accessible' : 'locked'}`}
          onClick={() => handleRoomClick('treasury')}
          data-testid="nav-treasury"
          title="Treasury"
        >
          <span className="nav-icon">{canAccessRoom('treasury') ? '💰' : '🔒'}</span>
          <span className="nav-name">Treasury</span>
        </button>
      </div>

      {/* Rules Modal */}
      {showRulesModal && (
        <div className="modal-overlay" onClick={() => setShowRulesModal(false)}>
          <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRulesModal(false)}>×</button>
            <h3>The Arcolia Code & Oath</h3>
            <div className="rules-content">
              <pre>{oathText}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Donate Modal */}
      {showDonateModal && (
        <div className="modal-overlay" onClick={() => setShowDonateModal(false)}>
          <div className="donate-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDonateModal(false)}>×</button>
            <h3>Support Arcolia</h3>
            <p className="donate-subtitle">Donations support the continued growth of Arcolia. Contributors may receive ranking upgrades based on their generosity.</p>
            
            <div className="treasury-info">
              <label>Treasury Wallet (Polygon)</label>
              <div className="wallet-display">
                <code>{TREASURY_WALLET}</code>
                <button onClick={() => navigator.clipboard.writeText(TREASURY_WALLET)} className="copy-btn">
                  📋
                </button>
              </div>
            </div>
            
            <div className="donation-form">
              <div className="form-row">
                <input
                  type="number"
                  placeholder="Amount"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  className="donation-input"
                />
                <select 
                  value={donationCurrency} 
                  onChange={(e) => setDonationCurrency(e.target.value)}
                  className="currency-select"
                >
                  {ACCEPTED_CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              
              <input
                type="text"
                placeholder="Transaction Hash (optional)"
                value={donationTxHash}
                onChange={(e) => setDonationTxHash(e.target.value)}
                className="tx-hash-input"
              />
              
              {donationError && <div className="error-message">{donationError}</div>}
              {donationSuccess && <div className="success-message">{donationSuccess}</div>}
              
              <button className="submit-donation-btn" onClick={submitDonation}>
                Record Donation
              </button>
              
              <p className="donation-note">
                Send your donation to the treasury wallet on the <strong>Polygon network</strong>, then record it here. A Founder will verify and may upgrade your ranking.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* The Commons Chat Modal - Discord-style */}
      {showChatModal && currentChatRoom && (
        <div className="modal-overlay commons-overlay" onClick={closeChatRoom}>
          <div className="commons-modal" onClick={(e) => e.stopPropagation()} data-testid="commons-modal">
            
            {/* Mobile Close Button - for iOS */}
            <button className="commons-mobile-close" onClick={closeChatRoom} aria-label="Close">
              ×
            </button>
            
            {/* Left Sidebar - Profile, Channels, Guidelines */}
            <div className="commons-sidebar-left">
              {/* Arcolia Logo */}
              <div className="commons-logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_page-builder-225/artifacts/nycgkxb6_Arcolia%20Logo.png"
                  alt="Arcolia"
                  className="commons-logo-img"
                />
              </div>

              {/* User Profile Card */}
              <div className="commons-profile">
                <div className="profile-avatar">
                  {user?.username?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="profile-info">
                  <span className="profile-name">{user?.username || 'Member'}</span>
                  {walletAddress && (
                    <span className="profile-wallet">{truncateWallet(walletAddress)}</span>
                  )}
                </div>
              </div>

              {/* ARCO Balance */}
              <div className="commons-balance">
                <span className="balance-label">ARCO Balance</span>
                <div className="balance-value">
                  <span className="balance-amount">{arcoBalance !== null ? arcoBalance.toLocaleString() : '0'}</span>
                  <span className="balance-icon">◈</span>
                </div>
                <span className="balance-rank">{getMemberStatus()}</span>
              </div>

              {/* Chat Channels */}
              <div className="commons-channels">
                <div className="channels-header">CHANNELS</div>
                {chatChannels.map(channel => (
                  <button
                    key={channel.id}
                    className={`channel-btn ${currentChannel === channel.id ? 'active' : ''}`}
                    onClick={() => switchChannel(channel.id)}
                    data-testid={`channel-${channel.id}`}
                  >
                    <span className="channel-hash">#</span>
                    <span className="channel-name">{channel.name}</span>
                  </button>
                ))}
              </div>

              {/* Community Guidelines */}
              <div className="commons-guidelines">
                <button 
                  className="guidelines-btn"
                  onClick={() => setShowGuidelines(!showGuidelines)}
                >
                  <span className="guidelines-icon">📜</span>
                  <span>Community Guidelines</span>
                  <span className="guidelines-arrow">{showGuidelines ? '▼' : '▶'}</span>
                </button>
                {showGuidelines && (
                  <div className="guidelines-content">
                    {COMMUNITY_GUIDELINES.map((rule) => (
                      <p key={rule} className="guideline-item">• {rule}</p>
                    ))}
                  </div>
                )}
              </div>

              {/* Inspirational Quote */}
              <div className="commons-quote">
                <p>&quot;Strength is found in unity. Wisdom is forged in dialogue.&quot;</p>
              </div>

              {/* Disconnect Button */}
              <button className="commons-disconnect" onClick={closeChatRoom} data-testid="disconnect-btn">
                DISCONNECT
              </button>
            </div>

            {/* Center - Main Area */}
            <div className="commons-main">
              {/* Header */}
              <div className="commons-header">
                <div className="commons-header-content">
                  <h1 className="commons-title">THE COMMONS</h1>
                  <p className="commons-subtitle">Where voices gather. Where ideas grow. Where Arcolia lives.</p>
                </div>
                {user?.role === 'Founder' && commonsMainTab === 'chat' && (
                  <button 
                    className="commons-settings-btn"
                    onClick={() => setShowChannelSettings(!showChannelSettings)}
                    title="Channel Settings"
                    data-testid="channel-settings-btn"
                  >
                    ⚙
                  </button>
                )}
                {user?.role === 'Founder' && commonsMainTab === 'forum' && (
                  <button 
                    className="commons-settings-btn"
                    onClick={() => setShowForumSettings(!showForumSettings)}
                    title="Forum Settings"
                    data-testid="forum-settings-btn"
                  >
                    ⚙
                  </button>
                )}
              </div>

              {/* Main Tabs - Chat vs Forum */}
              <div className="commons-main-tabs">
                <button 
                  className={`main-tab-btn ${commonsMainTab === 'chat' ? 'active' : ''}`}
                  onClick={() => setCommonsMainTab('chat')}
                  data-testid="commons-chat-tab"
                >
                  💬 Chat
                </button>
                <button 
                  className={`main-tab-btn ${commonsMainTab === 'forum' ? 'active' : ''}`}
                  onClick={() => setCommonsMainTab('forum')}
                  data-testid="commons-forum-tab"
                >
                  📝 Forum
                </button>
              </div>

              {/* ========== CHAT TAB CONTENT ========== */}
              {commonsMainTab === 'chat' && (
                <>
                  {/* Channel Settings Panel (Founder only) */}
                  {showChannelSettings && user?.role === 'Founder' && (
                <div className="channel-settings-panel">
                  <div className="channel-settings-header">
                    <h3>Channel Settings</h3>
                    <button className="close-settings-btn" onClick={() => setShowChannelSettings(false)}>×</button>
                  </div>
                  
                  {channelSettingsError && (
                    <div className="channel-settings-error">{channelSettingsError}</div>
                  )}
                  
                  <div className="channel-add-form">
                    <input
                      type="text"
                      placeholder="New channel name..."
                      value={newChannelName}
                      onChange={(e) => setNewChannelName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addChannel()}
                      className="channel-name-input"
                    />
                    <button className="channel-add-btn" onClick={addChannel}>+ Add</button>
                  </div>
                  
                  <div className="channel-list-settings">
                    {chatChannels.map((channel, index) => (
                      <div key={channel.id} className="channel-setting-item">
                        <div className="channel-reorder">
                          <button 
                            className="reorder-btn" 
                            onClick={() => moveChannel(index, -1)}
                            disabled={index === 0}
                          >▲</button>
                          <button 
                            className="reorder-btn" 
                            onClick={() => moveChannel(index, 1)}
                            disabled={index === chatChannels.length - 1}
                          >▼</button>
                        </div>
                        
                        {editingChannel === channel.id ? (
                          <input
                            type="text"
                            defaultValue={channel.name}
                            onBlur={(e) => renameChannel(channel.id, e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && renameChannel(channel.id, e.target.value)}
                            className="channel-rename-input"
                            autoFocus
                          />
                        ) : (
                          <span className="channel-setting-name" onClick={() => setEditingChannel(channel.id)}>
                            # {channel.name}
                          </span>
                        )}
                        
                        <div className="channel-actions">
                          <button 
                            className="channel-rename-btn"
                            onClick={() => setEditingChannel(channel.id)}
                            title="Rename"
                          >✏️</button>
                          <button 
                            className="channel-delete-btn"
                            onClick={() => deleteChannel(channel.id)}
                            title="Delete"
                            disabled={channel.id === 'general'}
                          >🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Channel Tabs */}
              <div className="commons-tabs">
                {chatChannels.map(channel => (
                  <button
                    key={channel.id}
                    className={`tab-btn ${currentChannel === channel.id ? 'active' : ''}`}
                    onClick={() => switchChannel(channel.id)}
                  >
                    # {channel.name}
                  </button>
                ))}
              </div>

              {/* Messages Area */}
              <div className="commons-messages" data-testid="commons-messages">
                {chatLoading ? (
                  <div className="messages-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading messages...</span>
                  </div>
                ) : chatMessages.length === 0 ? (
                  <div className="messages-empty">
                    <span className="empty-icon">💬</span>
                    <p>No messages in #{currentChannel} yet.</p>
                    <p className="empty-hint">Be the first to speak!</p>
                  </div>
                ) : (
                  chatMessages.map((msg) => (
                    <div key={msg.id || `${msg.user_id}-${msg.created_at}`} className={`commons-message ${msg.user_id === user?.id ? 'own-message' : ''}`}>
                      <div className="message-avatar">
                        {msg.username?.charAt(0).toUpperCase() || 'A'}
                      </div>
                      <div className="message-body">
                        <div className="message-meta">
                          <span className="message-author">{msg.username}</span>
                          <span className={`message-role role-${(msg.role || 'Member').toLowerCase()}`}>
                            {msg.role || 'Member'}
                          </span>
                          <span className="message-time">{formatMessageTime(msg.created_at)}</span>
                        </div>
                        <div className="message-text">{msg.content}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Message Input */}
              <div className="commons-input-area">
                <input
                  type="text"
                  placeholder={`Message #${currentChannel}...`}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="commons-input"
                  data-testid="message-input"
                />
                <button className="commons-send-btn" onClick={sendMessage} data-testid="send-btn">
                  SEND
                </button>
              </div>
                </>
              )}

              {/* ========== FORUM TAB CONTENT ========== */}
              {commonsMainTab === 'forum' && (
                <div className="forum-container">
                  {/* Forum Settings Panel (Founder only) */}
                  {showForumSettings && user?.role === 'Founder' && (
                    <div className="forum-settings-panel">
                      <div className="forum-settings-header">
                        <h3>Forum Settings</h3>
                        <button className="close-settings-btn" onClick={() => setShowForumSettings(false)}>×</button>
                      </div>
                      
                      <div className="forum-settings-tabs">
                        <button 
                          className={`forum-settings-tab ${forumSettingsTab === 'categories' ? 'active' : ''}`}
                          onClick={() => setForumSettingsTab('categories')}
                        >
                          Categories
                        </button>
                        <button 
                          className={`forum-settings-tab ${forumSettingsTab === 'history' ? 'active' : ''}`}
                          onClick={() => setForumSettingsTab('history')}
                        >
                          History
                        </button>
                      </div>

                      {forumSettingsTab === 'categories' && (
                        <div className="forum-settings-content">
                          <div className="category-list">
                            {forumCategories.map(cat => (
                              <div key={cat.id} className="category-item">
                                <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                                <span className="category-name">{cat.name}</span>
                                {cat.id !== 'general' && (
                                  <button className="remove-category-btn" onClick={() => removeForumCategory(cat.id)}>×</button>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="add-category-form">
                            <input
                              type="text"
                              placeholder="New category..."
                              value={newForumCategoryName}
                              onChange={(e) => setNewForumCategoryName(e.target.value)}
                              className="category-name-input"
                            />
                            <input
                              type="color"
                              value={newForumCategoryColor}
                              onChange={(e) => setNewForumCategoryColor(e.target.value)}
                              className="category-color-input"
                            />
                            <button className="add-category-btn" onClick={addForumCategory}>Add</button>
                          </div>
                        </div>
                      )}

                      {forumSettingsTab === 'history' && (
                        <div className="forum-settings-content">
                          <div className="history-list">
                            {forumHistory.length === 0 ? (
                              <p className="no-history">No deleted discussions</p>
                            ) : (
                              forumHistory.map(disc => (
                                <div key={disc.id} className="history-item">
                                  <div className="history-info">
                                    <span className="history-title">{disc.title}</span>
                                    <span className="history-meta">by {disc.author} • {disc.reply_count} replies</span>
                                  </div>
                                  <div className="history-actions">
                                    <button className="restore-btn" onClick={() => restoreForumDiscussion(disc.id)}>Restore</button>
                                    <button className="permanent-delete-btn" onClick={() => permanentDeleteForumDiscussion(disc.id)}>Delete</button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Forum Header with Search and Filter */}
                  <div className="forum-header">
                    <div className="forum-filters">
                      <input
                        type="text"
                        placeholder="Search discussions..."
                        value={forumSearchQuery}
                        onChange={(e) => {
                          setForumSearchQuery(e.target.value);
                          fetchForumDiscussions(e.target.value, selectedForumCategory);
                        }}
                        className="forum-search"
                      />
                      <select 
                        value={selectedForumCategory}
                        onChange={(e) => {
                          setSelectedForumCategory(e.target.value);
                          fetchForumDiscussions(forumSearchQuery, e.target.value);
                        }}
                        className="forum-category-filter"
                      >
                        <option value="all">All Categories</option>
                        {forumCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    {canCreateForumPost() && (
                      <button 
                        className="new-forum-post-btn"
                        onClick={() => setShowNewForumPost(!showNewForumPost)}
                      >
                        + New Discussion
                      </button>
                    )}
                    {!canCreateForumPost() && (
                      <span className="forum-rank-notice">Inner Circle+ to post</span>
                    )}
                  </div>

                  {/* New Post Form */}
                  {showNewForumPost && (
                    <div className="new-forum-post-form">
                      <input
                        type="text"
                        placeholder="Discussion title..."
                        value={newForumTitle}
                        onChange={(e) => setNewForumTitle(e.target.value)}
                        className="forum-title-input"
                      />
                      <select 
                        value={newForumCategory}
                        onChange={(e) => setNewForumCategory(e.target.value)}
                        className="forum-category-select"
                      >
                        {forumCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                      <textarea
                        placeholder="Share your thoughts..."
                        value={newForumContent}
                        onChange={(e) => setNewForumContent(e.target.value)}
                        className="forum-content-input"
                        rows={4}
                      />
                      <div className="forum-form-actions">
                        <button className="forum-submit-btn" onClick={createForumDiscussion}>Create Discussion</button>
                        <button className="forum-cancel-btn" onClick={() => setShowNewForumPost(false)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Discussion List or Selected Discussion */}
                  {selectedForumDiscussion ? (
                    <div className="forum-discussion-view">
                      <button className="forum-back-btn" onClick={() => setSelectedForumDiscussion(null)}>
                        ← Back to discussions
                      </button>
                      
                      <div className="forum-discussion-full">
                        <span className="forum-category-badge" style={{ backgroundColor: getForumCategoryInfo(selectedForumDiscussion.category).color }}>
                          {getForumCategoryInfo(selectedForumDiscussion.category).name}
                        </span>
                        <h2>{selectedForumDiscussion.title}</h2>
                        <div className="forum-discussion-meta">
                          <span className="forum-author">{selectedForumDiscussion.author}</span>
                          <span className="forum-date">{formatForumDate(selectedForumDiscussion.created_at)}</span>
                        </div>
                        <div className="forum-discussion-content">{selectedForumDiscussion.content}</div>
                      </div>

                      <div className="forum-replies-section">
                        <h4>Replies ({forumReplies.length})</h4>
                        <div className="forum-replies-list">
                          {forumReplies.map(reply => (
                            <div key={reply.id} className="forum-reply">
                              <div className="forum-reply-meta">
                                <span className="forum-reply-author">{reply.author}</span>
                                <span className="forum-reply-role">{reply.author_role}</span>
                                <span className="forum-reply-date">{formatForumDate(reply.created_at)}</span>
                              </div>
                              <div className="forum-reply-content">{reply.content}</div>
                            </div>
                          ))}
                        </div>

                        <div className="forum-reply-form">
                          <textarea
                            placeholder="Write a reply..."
                            value={newForumReply}
                            onChange={(e) => setNewForumReply(e.target.value)}
                            className="forum-reply-input"
                            rows={3}
                          />
                          <button className="forum-reply-btn" onClick={addForumReply}>Reply</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="forum-discussions-list">
                      {forumLoading ? (
                        <div className="forum-loading">Loading discussions...</div>
                      ) : forumDiscussions.length === 0 ? (
                        <div className="forum-empty">
                          <span className="empty-icon">📝</span>
                          <p>No discussions yet.</p>
                          {canCreateForumPost() && <p className="empty-hint">Be the first to start a conversation!</p>}
                        </div>
                      ) : (
                        forumDiscussions.map(disc => (
                          <div 
                            key={disc.id} 
                            className={`forum-discussion-card ${disc.is_pinned ? 'pinned' : ''}`}
                            onClick={() => {
                              setSelectedForumDiscussion(disc);
                              fetchForumReplies(disc.id);
                            }}
                          >
                            {disc.is_pinned && <span className="forum-pin-badge">📌 Pinned</span>}
                            <div className="forum-discussion-header">
                              <span className="forum-category-badge" style={{ backgroundColor: getForumCategoryInfo(disc.category).color }}>
                                {getForumCategoryInfo(disc.category).name}
                              </span>
                              <h4 className="forum-discussion-title">{disc.title}</h4>
                            </div>
                            <p className="forum-discussion-preview">{disc.content.substring(0, 150)}...</p>
                            <div className="forum-discussion-meta">
                              <span className="forum-author">{disc.author}</span>
                              <span className="forum-date">{formatForumDate(disc.created_at)}</span>
                              <span className="forum-replies-count">{disc.reply_count || 0} replies</span>
                            </div>
                            {user?.role === 'Founder' && (
                              <div className="forum-founder-actions">
                                <button 
                                  className="forum-pin-btn"
                                  onClick={(e) => { e.stopPropagation(); togglePinForumDiscussion(disc.id); }}
                                >
                                  {disc.is_pinned ? 'Unpin' : 'Pin'}
                                </button>
                                <button 
                                  className="forum-delete-btn"
                                  onClick={(e) => { e.stopPropagation(); deleteForumDiscussion(disc.id); }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Sidebar - Members */}
            <div className="commons-sidebar-right">
              {/* Online Members Section */}
              <div className="members-section online-section">
                <div className="members-header online">
                  <span className="members-title">ONLINE</span>
                  <span className="members-count">— {onlineMembers.length}</span>
                </div>
                <div className="members-list">
                  {onlineMembers.length > 0 ? (
                    onlineMembers.map((member) => (
                      <div key={member.id || member.username} className="member-item">
                        <div className="member-avatar">
                          {member.username?.charAt(0).toUpperCase() || 'M'}
                          <span className="online-indicator online"></span>
                        </div>
                        <div className="member-info">
                          <span className="member-name">{member.username}</span>
                          <span className={`member-role role-${(member.role || 'Member').toLowerCase()}`}>
                            {member.role || 'Member'}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-members-msg">No members online</div>
                  )}
                </div>
              </div>

              {/* Offline Members Section */}
              <div className="members-section offline-section">
                <div className="members-header offline">
                  <span className="members-title">OFFLINE</span>
                  <span className="members-count">— {offlineMembers.length}</span>
                </div>
                <div className="members-list">
                  {offlineMembers.map((member) => (
                    <div key={member.id || member.username} className="member-item offline">
                      <div className="member-avatar">
                        {member.username?.charAt(0).toUpperCase() || 'M'}
                        <span className="online-indicator offline"></span>
                      </div>
                      <div className="member-info">
                        <span className="member-name">{member.username}</span>
                        <span className={`member-role role-${(member.role || 'Member').toLowerCase()}`}>
                          {member.role || 'Member'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connection Modal */}
      {showWalletModal && (
        <div className="modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowWalletModal(false)}>×</button>
            <h3>Connect Your Wallet</h3>
            <p>Connect your wallet to access deeper areas of Arcolia and verify your ARCO holdings.</p>
            
            {walletError && <div className="error-message">{walletError}</div>}
            
            <button 
              className="connect-wallet-btn"
              onClick={() => { connectWallet(); setShowWalletModal(false); }}
            >
              🔗 Connect Wallet
            </button>
            
            <p className="wallet-note">Supports MetaMask, WalletConnect, Coinbase Wallet, and more. Make sure you&apos;re on Polygon network.</p>
          </div>
        </div>
      )}

      {/* Admin Modal - Role Management */}
      {showAdminModal && isFounder && (
        <div className="modal-overlay" onClick={() => setShowAdminModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAdminModal(false)}>×</button>
            <h3>Manage Guild Roles</h3>
            <p className="admin-subtitle">As Founder, you can assign roles to guild members</p>
            
            {adminError && <div className="error-message">{adminError}</div>}
            {adminSuccess && <div className="success-message">{adminSuccess}</div>}
            
            {adminLoading ? (
              <div className="admin-loading">Loading members...</div>
            ) : (
              <div className="users-list">
                {allUsers.map((u) => (
                  <div key={u.id || u.user_id} className={`user-row ${u.is_banned ? 'user-banned' : ''}`}>
                    <div className="user-info">
                      <span className="user-name">
                        {u.avatar_url && <img src={u.avatar_url} alt="" className="user-mini-avatar" />}
                        {u.username}
                        {u.is_banned && <span className="banned-badge">BANNED</span>}
                        {!u.email_verified && <span className="unverified-badge">UNVERIFIED</span>}
                        {u.name_changes_count > 0 && (
                          <button 
                            className="name-history-btn"
                            onClick={() => fetchUsernameHistory(u.user_id, u.username)}
                            title="View username history"
                          >
                            📜 {u.name_changes_count}
                          </button>
                        )}
                      </span>
                      <span className="user-email">{u.email}</span>
                    </div>
                    <div className="user-actions">
                      <select 
                        value={u.role || 'Member'} 
                        onChange={(e) => updateUserRole(u.id || u.user_id, e.target.value)}
                        className="role-select"
                        disabled={u.role === 'Founder' && (u.id || u.user_id) !== user.id}
                      >
                        <option value="Founder">Founder</option>
                        <option value="Aurel">Aurel</option>
                        <option value="Archivist">Archivist</option>
                        <option value="Steward">Steward</option>
                        <option value="Inner Circle">Inner Circle</option>
                        <option value="Honored Member">Honored Member</option>
                        <option value="Member">Member</option>
                      </select>
                      {u.role !== 'Founder' && (u.id || u.user_id) !== user.id && (
                        <>
                          <button 
                            className={`action-btn verify-btn ${u.email_verified ? 'verified' : ''}`}
                            onClick={() => verifyUser(u.id || u.user_id)}
                            title={u.email_verified ? 'Remove verification' : 'Manually verify user'}
                            data-testid={`verify-btn-${u.id || u.user_id}`}
                          >
                            {u.email_verified ? '✓' : '📧'}
                          </button>
                          <button 
                            className={`action-btn ban-btn ${u.is_banned ? 'unban' : ''}`}
                            onClick={() => banUser(u.id || u.user_id)}
                            title={u.is_banned ? 'Unban user' : 'Ban user'}
                          >
                            {u.is_banned ? '✓' : '🚫'}
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => deleteUser(u.id || u.user_id, u.username)}
                            title="Delete user permanently"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal - Guild Configuration */}
      {showSettingsModal && isFounder && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSettingsModal(false)}>×</button>
            <h3>Guild Settings</h3>
            <p className="admin-subtitle">Configure roles and room access permissions</p>
            
            {settingsError && <div className="error-message">{settingsError}</div>}
            {settingsSuccess && <div className="success-message">{settingsSuccess}</div>}
            
            {settingsLoading ? (
              <div className="admin-loading">Loading settings...</div>
            ) : (
              <div className="settings-content">
                {/* Role Names Section */}
                <div className="settings-section">
                  <h4>Role Display Names</h4>
                  <p className="section-note">Customize how each role appears in the guild</p>
                  <div className="roles-list">
                    {sortedRoleEntries.map(([key, role]) => (
                      <div key={key} className="role-edit-row">
                        <span className="role-key">{key}</span>
                        <input
                          type="text"
                          value={role.displayName}
                          onChange={(e) => updateRoleDisplayName(key, e.target.value)}
                          className="role-name-input"
                          placeholder="Display name"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Room Access Section */}
                <div className="settings-section">
                  <h4>Room Access Permissions</h4>
                  <p className="section-note">Control which roles can access each room</p>
                  <div className="rooms-access-grid">
                    {Object.entries(editingRooms).map(([roomKey, room]) => (
                      <div key={roomKey} className="room-access-card">
                        <div className="room-header">
                          <span className="room-name">{room.name}</span>
                          <span className="room-desc">{room.description}</span>
                        </div>
                        <div className="room-roles-toggles">
                          {sortedRoleKeys.map(roleKey => (
                            <label key={roleKey} className="role-toggle">
                              <input
                                type="checkbox"
                                checked={room.allowedRoles?.includes(roleKey) || false}
                                onChange={() => toggleRoomAccess(roomKey, roleKey)}
                              />
                              <span className="role-toggle-label">{editingRoles[roleKey]?.displayName || roleKey}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Donations Management Section */}
                <div className="settings-section donations-section">
                  <h4>💎 Donations Management</h4>
                  <p className="section-note">Review and verify member donations</p>
                  
                  <div className="donations-tabs">
                    <button 
                      className={`donations-tab ${donationsTab === 'pending' ? 'active' : ''}`}
                      onClick={() => setDonationsTab('pending')}
                    >
                      Pending ({pendingDonations.length})
                    </button>
                    <button 
                      className={`donations-tab ${donationsTab === 'verified' ? 'active' : ''}`}
                      onClick={() => setDonationsTab('verified')}
                    >
                      Verified ({verifiedDonations.length})
                    </button>
                    <button 
                      className={`donations-tab ${donationsTab === 'history' ? 'active' : ''}`}
                      onClick={() => setDonationsTab('history')}
                    >
                      All History ({allDonations.length})
                    </button>
                  </div>
                  
                  <div className="donations-content">
                    {donationsLoading ? (
                      <div className="donations-loading">Loading donations...</div>
                    ) : (
                      <>
                        {/* Pending Donations */}
                        {donationsTab === 'pending' && (
                          <div className="donations-list">
                            {pendingDonations.length === 0 ? (
                              <div className="no-donations">No pending donations to review</div>
                            ) : (
                              pendingDonations.map(donation => (
                                <div key={donation.id} className="donation-card pending">
                                  <div className="donation-info">
                                    <span className="donation-user">{donation.username}</span>
                                    <span className="donation-amount">{donation.amount} {donation.currency}</span>
                                    <span className="donation-date">
                                      {new Date(donation.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {donation.tx_hash && (
                                    <a 
                                      href={`https://polygonscan.com/tx/${donation.tx_hash}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="donation-tx-link"
                                    >
                                      View TX ↗
                                    </a>
                                  )}
                                  <div className="donation-actions">
                                    <select 
                                      className="role-upgrade-select"
                                      id={`role-${donation.id}`}
                                      defaultValue=""
                                    >
                                      <option value="">No role change</option>
                                      {sortedRoleKeys.map(roleKey => (
                                        <option key={roleKey} value={roleKey}>
                                          Upgrade to {editingRoles[roleKey]?.displayName || roleKey}
                                        </option>
                                      ))}
                                    </select>
                                    <button 
                                      className="verify-btn"
                                      onClick={() => {
                                        const select = document.getElementById(`role-${donation.id}`);
                                        handleDonationAction(donation.id, 'verified', select?.value || null);
                                      }}
                                    >
                                      ✓ Verify
                                    </button>
                                    <button 
                                      className="reject-btn"
                                      onClick={() => handleDonationAction(donation.id, 'rejected')}
                                    >
                                      ✕ Reject
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                        
                        {/* Verified Donations */}
                        {donationsTab === 'verified' && (
                          <div className="donations-list">
                            {verifiedDonations.length === 0 ? (
                              <div className="no-donations">No verified donations yet</div>
                            ) : (
                              verifiedDonations.map(donation => (
                                <div key={donation.id} className="donation-card verified">
                                  <div className="donation-info">
                                    <span className="donation-user">{donation.username}</span>
                                    <span className="donation-amount">{donation.amount} {donation.currency}</span>
                                    <span className="donation-date">
                                      {new Date(donation.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="donation-status">
                                    <span className="status-badge verified">✓ Verified</span>
                                    {donation.verified_by && (
                                      <span className="verified-by">by {donation.verified_by}</span>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                        
                        {/* All Donations History */}
                        {donationsTab === 'history' && (
                          <div className="donations-list history">
                            {allDonations.length === 0 ? (
                              <div className="no-donations">No donation history</div>
                            ) : (
                              allDonations.map(donation => (
                                <div key={donation.id} className={`donation-card ${donation.status}`}>
                                  <div className="donation-info">
                                    <span className="donation-user">{donation.username}</span>
                                    <span className="donation-amount">{donation.amount} {donation.currency}</span>
                                    <span className="donation-date">
                                      {new Date(donation.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="donation-status">
                                    <span className={`status-badge ${donation.status}`}>
                                      {donation.status === 'verified' ? '✓ Verified' : 
                                       donation.status === 'rejected' ? '✕ Rejected' : '⏳ Pending'}
                                    </span>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <button className="save-settings-btn" onClick={saveSettings}>
                  Save Settings
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
            <h3>Your Profile</h3>
            
            {profileLoading ? (
              <div className="profile-loading">Loading profile...</div>
            ) : profileData ? (
              <div className="profile-content">
                {profileError && <div className="error-message">{profileError}</div>}
                {profileSuccess && <div className="success-message">{profileSuccess}</div>}
                
                {/* Current Avatar & Selection */}
                <div className="profile-section avatar-section">
                  <h4>Profile Avatar</h4>
                  <div className="current-avatar">
                    {profileData.avatar_url ? (
                      <img src={profileData.avatar_url} alt="Current Avatar" className="current-avatar-img" />
                    ) : (
                      <div className="no-avatar">
                        {profileData.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  {selectedAvatarInfo ? (
                    <div className="avatar-info-display">
                      <span className="avatar-name">{selectedAvatarInfo.name}</span>
                      <span className="avatar-unlocked-by">Unlocked at: <strong>{selectedAvatarInfo.unlocked_by}</strong></span>
                    </div>
                  ) : (
                    <p className="avatar-access-info">
                      {availableAvatars.length} / {profileData.total_avatars} avatars available for your rank
                    </p>
                  )}
                  <div className="avatar-grid">
                    {availableAvatars.map((avatar, index) => (
                      <div 
                        key={avatar.id}
                        className={`avatar-option ${profileData.avatar_id === avatar.id ? 'selected' : ''}`}
                        onClick={() => selectAvatar(avatar.id)}
                        title={avatar.name}
                      >
                        <img src={avatar.url} alt={avatar.name} />
                        {profileData.avatar_id === avatar.id && <span className="selected-check">✓</span>}
                      </div>
                    ))}
                    {profileData.total_avatars > availableAvatars.length && (
                      <div className="locked-avatars-hint">
                        +{profileData.total_avatars - availableAvatars.length} more with higher ranks
                      </div>
                    )}
                  </div>
                </div>

                {/* Achievements Section */}
                <div className="profile-section achievements-section">
                  <h4>Achievements ({userAchievements.length}/{allAchievements.length})</h4>
                  
                  {/* Pending Rewards Banner */}
                  {pendingRewards.total > 0 && (
                    <div className="pending-rewards-banner" data-testid="pending-rewards-banner">
                      <div className="pending-info">
                        <span className="pending-amount">{pendingRewards.total} ARCO</span>
                        <span className="pending-label">pending from {pendingRewards.count} achievement{pendingRewards.count !== 1 ? 's' : ''}</span>
                      </div>
                      {isWeb3Connected ? (
                        <button 
                          className="claim-rewards-btn"
                          onClick={claimPendingRewards}
                          disabled={claimingRewards}
                          data-testid="claim-rewards-btn"
                        >
                          {claimingRewards ? 'Claiming...' : 'Claim Rewards'}
                        </button>
                      ) : (
                        <div className="connect-wallet-hint">
                          <span>Connect wallet to claim</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Display Badge Selection */}
                  <div className="display-badge-section">
                    <label>Display Badge (shown next to your name):</label>
                    <div className="display-badge-options">
                      <button 
                        className={`badge-option ${!displayBadge ? 'selected' : ''}`}
                        onClick={() => setMyDisplayBadge(null)}
                      >
                        None
                      </button>
                      {userAchievements.map(achievement => (
                        <button
                          key={achievement.id}
                          className={`badge-option ${displayBadge?.id === achievement.id ? 'selected' : ''}`}
                          onClick={() => setMyDisplayBadge(achievement.id)}
                          title={achievement.name}
                        >
                          <img src={achievement.badge_url} alt={achievement.name} className="badge-mini" />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Achievement Grid */}
                  <div className="achievements-grid">
                    {allAchievements.map(achievement => (
                      <div 
                        key={achievement.id}
                        className={`achievement-card ${achievement.earned ? 'earned' : 'locked'}`}
                        title={achievement.description}
                      >
                        <img 
                          src={achievement.badge_url} 
                          alt={achievement.name} 
                          className="achievement-badge"
                          style={{ filter: achievement.earned ? 'none' : 'grayscale(100%) opacity(0.5)' }}
                        />
                        <span className="achievement-name">{achievement.name}</span>
                        <span className="achievement-reward">{achievement.reward_arco} ARCO</span>
                        {achievement.earned && (
                          <span className="earned-check">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Username Change */}
                <div className="profile-section username-section">
                  <h4>Username</h4>
                  <div className="current-username">
                    <span className="username-label">Current:</span>
                    <span className="username-value">{profileData.username}</span>
                  </div>
                  
                  {profileData.changes_remaining > 0 ? (
                    <>
                      <div className="username-change-info">
                        <span className="changes-remaining">
                          {profileData.changes_remaining} change{profileData.changes_remaining !== 1 ? 's' : ''} remaining
                        </span>
                        {profileData.next_change_free ? (
                          <span className="free-badge">First change is FREE!</span>
                        ) : (
                          <div className="price-info">
                            <span className="price-badge">£{profileData.change_price_gbp?.toFixed(2)} per change</span>
                            <span className="treasury-note">Funds go directly to the Treasury and stay within the ecosystem</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="username-change-form">
                        <input
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          placeholder="Enter new username..."
                          className="username-input"
                          maxLength={20}
                        />
                        {checkingUsername && <span className="checking">Checking...</span>}
                        {usernameAvailable !== null && !checkingUsername && (
                          <span className={`availability ${usernameAvailable.available ? 'available' : 'taken'}`}>
                            {usernameAvailable.available ? '✓ Available' : `✗ ${usernameAvailable.reason}`}
                          </span>
                        )}
                        <button
                          className="change-username-btn"
                          onClick={handleUsernameChange}
                          disabled={!usernameAvailable?.available || changingUsername}
                        >
                          {changingUsername ? 'Processing...' : 
                           profileData.next_change_free ? 'Change Username (Free)' : 
                           `Change Username (£${profileData.change_price_gbp?.toFixed(2)})`}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="no-changes-left">
                      Maximum username changes reached ({profileData.max_changes})
                    </div>
                  )}
                  
                  {profileData.username_changes?.length > 0 && (
                    <div className="username-history">
                      <h5>Your Name History</h5>
                      <ul>
                        {profileData.username_changes.map((change) => (
                          <li key={`${change.from}-${change.to}-${change.changed_at}`}>
                            <span className="old-name">{change.from}</span>
                            <span className="arrow">→</span>
                            <span className="new-name">{change.to}</span>
                            <span className="change-date">
                              {new Date(change.changed_at).toLocaleDateString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Account Info */}
                <div className="profile-section account-section">
                  <h4>Account</h4>
                  <div className="account-info">
                    <div className="info-row">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{profileData.email}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Role:</span>
                      <span className="info-value role-badge">{profileData.role}</span>
                    </div>
                  </div>
                  
                  {/* Change Password Section */}
                  <div className="password-section">
                    {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
                    
                    {!showPasswordForm ? (
                      <button 
                        className="change-password-toggle"
                        onClick={() => {
                          setShowPasswordForm(true);
                          setPasswordError(null);
                        }}
                      >
                        🔐 Change Password
                      </button>
                    ) : (
                      <div className="password-form">
                        <h5>Change Password</h5>
                        {passwordError && <div className="error-message small">{passwordError}</div>}
                        
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Current password"
                          className="password-input"
                        />
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="New password (min 8 characters)"
                          className="password-input"
                        />
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          className="password-input"
                        />
                        
                        <div className="password-actions">
                          <button 
                            className="password-submit-btn"
                            onClick={handleChangePassword}
                            disabled={changingPassword}
                          >
                            {changingPassword ? 'Changing...' : 'Update Password'}
                          </button>
                          <button 
                            className="password-cancel-btn"
                            onClick={() => {
                              setShowPasswordForm(false);
                              setCurrentPassword('');
                              setNewPassword('');
                              setConfirmPassword('');
                              setPasswordError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bulletin Subscription Section */}
                  <div className="bulletin-section">
                    <div className="bulletin-toggle-row">
                      <div className="bulletin-info">
                        <span className="bulletin-label">Guild Bulletin</span>
                        <span className="bulletin-description">Receive news and updates from the Guild</span>
                      </div>
                      <button 
                        className={`bulletin-toggle-btn ${bulletinOptedIn ? 'opted-in' : 'opted-out'}`}
                        onClick={handleBulletinToggle}
                        disabled={bulletinLoading}
                        data-testid="bulletin-toggle-btn"
                      >
                        {bulletinLoading ? '...' : (bulletinOptedIn ? 'Subscribed' : 'Subscribe')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="profile-error">Failed to load profile data</div>
            )}
          </div>
        </div>
      )}

      {/* Username History Modal (Founder only) */}
      {showUsernameHistory && isFounder && (
        <div className="modal-overlay" onClick={() => setShowUsernameHistory(false)}>
          <div className="username-history-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUsernameHistory(false)}>×</button>
            <h3>Username History</h3>
            {usernameHistoryData ? (
              <div className="history-content">
                <div className="history-header">
                  <span className="current-name">Current: <strong>{usernameHistoryData.current_username}</strong></span>
                  <span className="changes-count">
                    {usernameHistoryData.changes_remaining} changes remaining
                  </span>
                </div>
                {usernameHistoryData.username_history?.length > 0 ? (
                  <ul className="history-list">
                    {usernameHistoryData.username_history.map((change) => (
                      <li key={`${change.from}-${change.to}-${change.changed_at}`} className="history-item">
                        <span className="history-from">{change.from}</span>
                        <span className="history-arrow">→</span>
                        <span className="history-to">{change.to}</span>
                        <span className="history-date">
                          {new Date(change.changed_at).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </span>
                        {change.paid && <span className="paid-badge">Paid</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-history">No username changes recorded</p>
                )}
              </div>
            ) : (
              <div className="loading-history">Loading history...</div>
            )}
          </div>
        </div>
      )}

      {/* The Arcolian Exchange Modal */}
      {showExchange && (
        <Exchange
          user={user}
          token={token}
          arcoBalance={arcoBalance}
          onClose={() => setShowExchange(false)}
        />
      )}

      {/* Direct Messages Modal */}
      {showDirectMessages && (
        <DirectMessages
          user={user}
          token={token}
          onClose={() => {
            setShowDirectMessages(false);
            fetchUnreadCount();
          }}
        />
      )}

      {/* Award Achievement Modal (Steward+ only) */}
      {showAchievementModal && canAwardAchievements() && (
        <div className="modal-overlay" onClick={() => {
          setShowAchievementModal(false);
          setSelectedAwardUser('');
          setSelectedAchievement(null);
          setAwardReason('');
        }}>
          <div className="achievement-award-modal" onClick={(e) => e.stopPropagation()} data-testid="award-achievement-modal">
            <button 
              className="modal-close" 
              onClick={() => {
                setShowAchievementModal(false);
                setSelectedAwardUser('');
                setSelectedAchievement(null);
                setAwardReason('');
              }}
            >×</button>
            
            <h3>🏆 Award Achievement</h3>
            
            {profileError && <div className="error-message">{profileError}</div>}
            {profileSuccess && <div className="success-message">{profileSuccess}</div>}
            
            {/* User Selection */}
            <div className="award-section">
              <label>Select Member:</label>
              <select 
                className="award-user-select"
                value={selectedAwardUser}
                onChange={(e) => setSelectedAwardUser(e.target.value)}
                data-testid="award-user-select"
              >
                <option value="">-- Select a member --</option>
                {achievementUsers.map(u => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.username} ({u.role}) - {u.achievements_earned}/{u.total_achievements} badges {u.has_wallet ? '💰' : '(no wallet)'}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Achievement Selection */}
            {selectedAwardUser && (
              <div className="award-section">
                <label>Select Achievement to Award:</label>
                <div className="award-achievement-grid">
                  {allAchievements.length === 0 ? (
                    <p className="loading-text">Loading achievements...</p>
                  ) : (
                    allAchievements
                      .filter(a => a.id !== 'arcolia_legend') // Can't manually award Legend
                      .map(achievement => {
                        const selectedUserData = achievementUsers.find(u => u.user_id === selectedAwardUser);
                        const alreadyEarned = selectedUserData?.earned_ids?.includes(achievement.id);
                        const requiresRole = achievement.required_role;
                        const userRole = selectedUserData?.role;
                        const roleMatch = !requiresRole || userRole === requiresRole;
                        
                        return (
                          <div
                            key={achievement.id}
                            className={`award-achievement-option ${selectedAchievement?.id === achievement.id ? 'selected' : ''} ${alreadyEarned ? 'already-earned' : ''}`}
                            onClick={() => {
                              if (!alreadyEarned && roleMatch) {
                                setSelectedAchievement(achievement);
                              }
                            }}
                            title={alreadyEarned ? 'Already earned' : (!roleMatch ? `Requires ${requiresRole} role` : achievement.description)}
                            data-testid={`achievement-option-${achievement.id}`}
                          >
                            <img src={achievement.badge_url} alt={achievement.name} />
                            <span className="name">{achievement.name}</span>
                            <span className="reward">{achievement.reward_arco} ARCO</span>
                            {alreadyEarned && <span className="earned-tag">✓</span>}
                            {!roleMatch && <span className="role-req">{requiresRole}</span>}
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            )}
            
            {/* Award Reason */}
            {selectedAchievement && (
              <div className="award-section">
                <label>Reason for awarding (optional):</label>
                <textarea
                  className="award-reason-input"
                  value={awardReason}
                  onChange={(e) => setAwardReason(e.target.value)}
                  placeholder="Describe why this member deserves this achievement..."
                  rows={3}
                  data-testid="award-reason-input"
                />
              </div>
            )}
            
            {/* Award Button */}
            <div className="award-actions">
              <button
                className="award-btn"
                disabled={!selectedAwardUser || !selectedAchievement || awardingAchievement}
                onClick={async () => {
                  if (selectedAwardUser && selectedAchievement) {
                    const success = await awardAchievement(selectedAwardUser, selectedAchievement.id, awardReason);
                    if (success) {
                      // Reset form but keep modal open to award more
                      setSelectedAchievement(null);
                      setAwardReason('');
                      // Refresh achievements list
                      fetchAchievements();
                    }
                  }
                }}
                data-testid="award-submit-btn"
              >
                {awardingAchievement ? 'Awarding...' : `Award ${selectedAchievement?.name || 'Achievement'}`}
              </button>
              
              {selectedAchievement && (
                <div className="award-preview">
                  <img src={selectedAchievement.badge_url} alt={selectedAchievement.name} className="preview-badge" />
                  <div className="preview-info">
                    <strong>{selectedAchievement.name}</strong>
                    <p>{selectedAchievement.description}</p>
                    <span className="reward-amount">+{selectedAchievement.reward_arco} ARCO</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Achievement Unlock Notification Popup */}
      {achievementPopup && (
        <div className="achievement-unlock-popup" data-testid="achievement-unlock-popup">
          <div className="unlock-content">
            <div className="unlock-header">
              <span className="unlock-star">⭐</span>
              <span className="unlock-title">Achievement Unlocked!</span>
              <span className="unlock-star">⭐</span>
            </div>
            {achievementPopup.badge_url && (
              <img src={achievementPopup.badge_url} alt={achievementPopup.name} className="unlock-badge" />
            )}
            <h3 className="unlock-name">{achievementPopup.name}</h3>
            {achievementPopup.reward_arco > 0 && (
              <div className="unlock-reward">
                {achievementPopup.reward_sent ? (
                  <span className="reward-sent">+{achievementPopup.reward_arco} ARCO Sent!</span>
                ) : (
                  <span className="reward-pending">+{achievementPopup.reward_arco} ARCO (connect wallet to claim)</span>
                )}
              </div>
            )}
            <button className="unlock-dismiss" onClick={() => setAchievementPopup(null)}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* The Journey Modal */}
      {showJourneyModal && (
        <div className="modal-overlay" onClick={() => setShowJourneyModal(false)}>
          <div className="journey-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowJourneyModal(false)}>×</button>
            
            <div className="journey-header">
              <h2>✦ The Journey</h2>
              <p className="journey-subtitle">Ways you can help Arcolia grow</p>
            </div>

            {/* Journey Tabs */}
            <div className="journey-tabs">
              <button 
                className={`journey-tab ${journeyTab === 'tasks' ? 'active' : ''}`}
                onClick={() => setJourneyTab('tasks')}
              >
                Tasks
              </button>
              <button 
                className={`journey-tab ${journeyTab === 'my-submissions' ? 'active' : ''}`}
                onClick={() => setJourneyTab('my-submissions')}
              >
                My Submissions
                {journeySubmissions.filter(s => s.status === 'pending_review' || s.status === 'pending_witnesses').length > 0 && (
                  <span className="tab-badge">{journeySubmissions.filter(s => s.status === 'pending_review' || s.status === 'pending_witnesses').length}</span>
                )}
              </button>
              <button 
                className={`journey-tab ${journeyTab === 'witness-requests' ? 'active' : ''}`}
                onClick={() => setJourneyTab('witness-requests')}
              >
                Witness Requests
                {pendingWitnessRequests.length > 0 && (
                  <span className="tab-badge">{pendingWitnessRequests.length}</span>
                )}
              </button>
              {['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role) && (
                <button 
                  className={`journey-tab ${journeyTab === 'review' ? 'active' : ''}`}
                  onClick={() => { setJourneyTab('review'); fetchJourneyReviewQueue(); }}
                >
                  Review Queue
                  {journeyReviewQueue.length > 0 && (
                    <span className="tab-badge">{journeyReviewQueue.length}</span>
                  )}
                </button>
              )}
              {/* Trial of Promotion Tab - visible to all */}
              <button 
                className={`journey-tab ${journeyTab === 'trial' ? 'active' : ''}`}
                onClick={() => setJourneyTab('trial')}
              >
                Trial of Promotion
                {myTrial?.has_active_trial && (
                  <span className="tab-badge trial-badge">Active</span>
                )}
              </button>
              {/* Trial Reviews Tab - Steward+ only */}
              {['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role) && (
                <button 
                  className={`journey-tab ${journeyTab === 'trial-reviews' ? 'active' : ''}`}
                  onClick={() => { setJourneyTab('trial-reviews'); fetchTrialReviews(); }}
                >
                  Trial Reviews
                  {trialReviews.active?.length > 0 && (
                    <span className="tab-badge">{trialReviews.active.length}</span>
                  )}
                </button>
              )}
            </div>

            {/* Success/Error Messages */}
            {journeySuccess && <div className="journey-success">{journeySuccess}</div>}
            {journeyError && <div className="journey-error">{journeyError}</div>}

            {/* Journey Content */}
            <div className="journey-content">
              {journeyLoading ? (
                <div className="journey-loading">Loading...</div>
              ) : (
                <>
                  {/* Tasks Tab */}
                  {journeyTab === 'tasks' && (
                    <div className="journey-tasks-list">
                      {/* Group tasks by room */}
                      {['Commons', 'Archives', 'Sanctuary', 'Treasury', 'Council Chamber'].map(room => {
                        const roomTasks = journeyTasks.filter(t => t.room === room);
                        if (roomTasks.length === 0) return null;
                        return (
                          <div key={room} className="journey-room-group">
                            <h3 className="room-heading">{room}</h3>
                            {roomTasks.map(task => (
                              <div key={task.id} className="journey-task-item">
                                <div className="task-info">
                                  <h4 className="task-title">{task.title}</h4>
                                  <p className="task-description">{task.description}</p>
                                  {task.witnesses_required > 0 && (
                                    <span className="witness-badge">Requires {task.witnesses_required} witness{task.witnesses_required > 1 ? 'es' : ''}</span>
                                  )}
                                </div>
                                <button 
                                  className="submit-task-btn"
                                  onClick={() => { setSelectedJourneyTask(task); setShowJourneySubmitModal(true); }}
                                >
                                  Submit Proof
                                </button>
                              </div>
                            ))}
                          </div>
                        );
                      })}

                      {/* Gift Button for Steward+ */}
                      {['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role) && (
                        <div className="journey-gift-section">
                          <button 
                            className="journey-gift-btn"
                            onClick={() => { setShowJourneyGiftModal(true); fetchAllUsers(); }}
                          >
                            🎁 Award Task Completion
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* My Submissions Tab */}
                  {journeyTab === 'my-submissions' && (
                    <div className="journey-submissions-list">
                      {journeySubmissions.length === 0 ? (
                        <p className="no-submissions">You haven't submitted any tasks yet.</p>
                      ) : (
                        journeySubmissions.map(sub => (
                          <div key={sub.id} className={`submission-item status-${sub.status}`}>
                            <div className="submission-header">
                              <span className="submission-task">{sub.task_title}</span>
                              <span className="submission-room">{sub.task_room}</span>
                            </div>
                            <p className="submission-proof">{sub.proof_description}</p>
                            {sub.has_proof_file && (
                              <span className="has-file">📎 {sub.proof_file_name}</span>
                            )}
                            {sub.witnesses && sub.witnesses.length > 0 && (
                              <div className="submission-witnesses">
                                <span>Witnesses: </span>
                                {sub.witnesses.map((w, i) => (
                                  <span key={i} className={`witness-status ${w.confirmed ? 'confirmed' : 'pending'}`}>
                                    {w.username} {w.confirmed ? '✓' : '⏳'}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="submission-footer">
                              <span className={`status-badge ${sub.status}`}>
                                {sub.status === 'pending_witnesses' && '⏳ Awaiting Witnesses'}
                                {sub.status === 'pending_review' && '⏳ Awaiting Review'}
                                {sub.status === 'approved' && '✓ Approved'}
                                {sub.status === 'denied' && '✗ Denied'}
                              </span>
                              <span className="submission-date">
                                {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                            {sub.review_notes && (
                              <p className="review-notes">Note: {sub.review_notes}</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Witness Requests Tab */}
                  {journeyTab === 'witness-requests' && (
                    <div className="journey-witness-list">
                      {pendingWitnessRequests.length === 0 ? (
                        <p className="no-requests">No pending witness requests.</p>
                      ) : (
                        pendingWitnessRequests.map(req => (
                          <div key={req.id} className="witness-request-item">
                            <div className="request-header">
                              <span className="requester">{req.submitter_username}</span>
                              <span className="request-task">{req.task_title}</span>
                            </div>
                            <p className="request-proof">{req.proof_description}</p>
                            <div className="witness-oath">
                              <p className="oath-text">
                                Do you swear by the Charter that this submission is truthful?
                              </p>
                              <div className="witness-actions">
                                <button 
                                  className="witness-confirm-btn"
                                  onClick={() => confirmAsWitness(req.submission_id, true)}
                                >
                                  ✓ I Confirm
                                </button>
                                <button 
                                  className="witness-decline-btn"
                                  onClick={() => confirmAsWitness(req.submission_id, false)}
                                >
                                  ✗ Decline
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Review Queue Tab (Steward+) */}
                  {journeyTab === 'review' && ['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role) && (
                    <div className="journey-review-list">
                      {journeyReviewQueue.length === 0 ? (
                        <p className="no-reviews">No submissions awaiting review.</p>
                      ) : (
                        journeyReviewQueue.map(sub => (
                          <div key={sub.id} className="review-item">
                            <div className="review-header">
                              <span className="review-user">{sub.username}</span>
                              <span className="review-task">{sub.task_title}</span>
                              <span className="review-room">{sub.task_room}</span>
                            </div>
                            <p className="review-proof">{sub.proof_description}</p>
                            {sub.has_proof_file && (
                              <a 
                                href={`${API_URL}/api/journey/proof-file/${sub.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="download-proof"
                              >
                                📎 Download: {sub.proof_file_name}
                              </a>
                            )}
                            {sub.witnesses && sub.witnesses.length > 0 && (
                              <div className="review-witnesses">
                                <strong>Witness Confirmations:</strong>
                                {sub.witnesses.map((w, i) => (
                                  <div key={i} className="witness-confirmation">
                                    <span className="witness-name">{w.username}:</span>
                                    <span className={w.confirmed ? 'confirmed' : 'pending'}>
                                      {w.confirmed ? `✓ "${w.confirmation_text}"` : '⏳ Pending'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="review-reward">
                              <span>Reward: {sub.reward_arco} ARCO</span>
                            </div>
                            <div className="review-actions">
                              <button 
                                className="approve-btn"
                                onClick={() => approveJourneySubmission(sub.id)}
                              >
                                ✓ Approve
                              </button>
                              <button 
                                className="deny-btn"
                                onClick={() => denyJourneySubmission(sub.id)}
                              >
                                ✗ Deny
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Trial of Promotion Tab */}
                  {journeyTab === 'trial' && trialContent && (
                    <div className="trial-tab-content">
                      {trialSuccess && <div className="trial-success">{trialSuccess}</div>}
                      {trialError && <div className="trial-error">{trialError}</div>}
                      
                      {/* Pre-trial info (if no active trial) */}
                      {!myTrial?.has_active_trial && (
                        <div className="trial-intro">
                          <div className="trial-section">
                            <h3>Purpose</h3>
                            <p>{trialContent.purpose}</p>
                          </div>
                          <div className="trial-section">
                            <h3>First Principle</h3>
                            <p>{trialContent.first_principle}</p>
                          </div>
                          <div className="trial-section">
                            <h3>What Promotion Tests</h3>
                            <p>{trialContent.what_promotion_tests?.intro}</p>
                            <ul className="trial-tests-list">
                              {trialContent.what_promotion_tests?.tests?.map((test, i) => (
                                <li key={i}><strong>{test.name}:</strong> {test.description}</li>
                              ))}
                            </ul>
                            <p className="trial-closing">{trialContent.what_promotion_tests?.closing}</p>
                          </div>
                          <div className="trial-section">
                            <h3>Eligibility Standard</h3>
                            <p>{trialContent.eligibility_standard}</p>
                          </div>
                          
                          {myTrial?.can_start_trial ? (
                            <div className="trial-start-section">
                              <p className="trial-target">
                                You are currently a <strong>{myTrial.current_role}</strong>. 
                                This trial will evaluate your readiness to become a <strong>{myTrial.next_role}</strong>.
                              </p>
                              {!showTrialStartConfirm ? (
                                <button 
                                  className="trial-start-btn"
                                  onClick={() => setShowTrialStartConfirm(true)}
                                >
                                  I am ready to undergo my trial
                                </button>
                              ) : (
                                <div className="trial-confirm-box">
                                  <p>Are you certain you wish to begin? This is a serious undertaking.</p>
                                  <div className="confirm-buttons">
                                    <button 
                                      className="confirm-yes"
                                      onClick={startTrial}
                                      disabled={trialLoading}
                                    >
                                      {trialLoading ? 'Starting...' : 'Yes, begin my trial'}
                                    </button>
                                    <button 
                                      className="confirm-no"
                                      onClick={() => setShowTrialStartConfirm(false)}
                                    >
                                      Not yet
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="trial-ineligible">
                              <p>Your current role ({user?.role}) cannot be promoted through this trial system.</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Active trial stages */}
                      {myTrial?.has_active_trial && (
                        <div className="trial-active">
                          <div className="trial-progress-header">
                            <h3>Your Trial: {myTrial.current_role} → {myTrial.target_role}</h3>
                            <div className="trial-stage-indicator">
                              Stage {myTrial.current_stage} of 5
                            </div>
                          </div>

                          {/* Stage Progress Bar */}
                          <div className="trial-stages-progress">
                            {[1, 2, 3, 4, 5].map(stage => {
                              const status = myTrial.stage_statuses?.[`stage_${stage}`];
                              return (
                                <div 
                                  key={stage} 
                                  className={`stage-indicator ${status} ${myTrial.current_stage === stage ? 'current' : ''}`}
                                >
                                  <span className="stage-num">{stage}</span>
                                  <span className="stage-name">
                                    {stage === 1 && 'Reflections'}
                                    {stage === 2 && 'Contributions'}
                                    {stage === 3 && 'Ethics'}
                                    {stage === 4 && 'Endorsements'}
                                    {stage === 5 && 'Affirmation'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Stage 1: Written Reflections */}
                          {myTrial.stage_statuses?.stage_1 === 'in_progress' || myTrial.stage_statuses?.stage_1 === 'revision_requested' ? (
                            <div className="trial-stage-form">
                              <h4>Stage 1: Written Reflections</h4>
                              {myTrial.stage_statuses?.stage_1 === 'revision_requested' && (
                                <div className="revision-notice">
                                  <strong>Revision Requested:</strong> {myTrial.review_notes?.stage_1}
                                </div>
                              )}
                              <p className="stage-instruction">Please provide thoughtful, honest responses to each reflection prompt.</p>
                              
                              {trialContent.stage_1_reflections?.map((prompt, i) => (
                                <div key={i} className="reflection-field">
                                  <label>{prompt}</label>
                                  <textarea
                                    value={stage1Form[['contributed', 'learned', 'changed_mind', 'arcolia_purpose', 'why_responsibility'][i]] || ''}
                                    onChange={(e) => setStage1Form({
                                      ...stage1Form,
                                      [['contributed', 'learned', 'changed_mind', 'arcolia_purpose', 'why_responsibility'][i]]: e.target.value
                                    })}
                                    rows={4}
                                    placeholder="Take your time with this reflection..."
                                  />
                                </div>
                              ))}
                              
                              <button 
                                className="trial-submit-btn"
                                onClick={submitStage1}
                                disabled={trialLoading || !Object.values(stage1Form).every(v => v.trim())}
                              >
                                {trialLoading ? 'Submitting...' : 'Submit Stage 1'}
                              </button>
                            </div>
                          ) : myTrial.stage_statuses?.stage_1 === 'submitted' ? (
                            <div className="trial-stage-status">
                              <h4>Stage 1: Written Reflections</h4>
                              <p className="awaiting">Awaiting review...</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_1 === 'approved' && myTrial.current_stage > 1 ? (
                            <div className="trial-stage-status approved">
                              <h4>Stage 1: Written Reflections ✓</h4>
                              <p>Approved</p>
                            </div>
                          ) : null}

                          {/* Stage 2: Contribution Review */}
                          {myTrial.stage_statuses?.stage_2 === 'in_progress' || myTrial.stage_statuses?.stage_2 === 'revision_requested' ? (
                            <div className="trial-stage-form">
                              <h4>Stage 2: Contribution Review</h4>
                              {myTrial.stage_statuses?.stage_2 === 'revision_requested' && (
                                <div className="revision-notice">
                                  <strong>Revision Requested:</strong> {myTrial.review_notes?.stage_2}
                                </div>
                              )}
                              <p className="stage-instruction">{trialContent.stage_2_description}</p>
                              
                              <div className="contribution-field">
                                <label>Describe your contributions:</label>
                                <textarea
                                  value={stage2Description}
                                  onChange={(e) => setStage2Description(e.target.value)}
                                  rows={6}
                                  placeholder="Detail your meaningful contributions to Arcolia..."
                                />
                              </div>
                              
                              <div className="file-upload-area">
                                <label>Upload supporting evidence (optional):</label>
                                <input 
                                  type="file" 
                                  multiple
                                  onChange={(e) => {
                                    const files = Array.from(e.target.files);
                                    const fileData = [];
                                    files.forEach(file => {
                                      const reader = new FileReader();
                                      reader.onload = () => {
                                        fileData.push({
                                          filename: file.name,
                                          file_data: reader.result,
                                          file_type: file.type
                                        });
                                        if (fileData.length === files.length) {
                                          setStage2Files(fileData);
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    });
                                  }}
                                />
                                {stage2Files.length > 0 && (
                                  <div className="uploaded-files">
                                    {stage2Files.map((f, i) => (
                                      <span key={i} className="file-chip">{f.filename}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <button 
                                className="trial-submit-btn"
                                onClick={submitStage2}
                                disabled={trialLoading || (!stage2Description.trim() && stage2Files.length === 0)}
                              >
                                {trialLoading ? 'Submitting...' : 'Submit Stage 2'}
                              </button>
                            </div>
                          ) : myTrial.stage_statuses?.stage_2 === 'submitted' ? (
                            <div className="trial-stage-status">
                              <h4>Stage 2: Contribution Review</h4>
                              <p className="awaiting">Awaiting review...</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_2 === 'approved' && myTrial.current_stage > 2 ? (
                            <div className="trial-stage-status approved">
                              <h4>Stage 2: Contribution Review ✓</h4>
                              <p>Approved</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_2 === 'locked' ? (
                            <div className="trial-stage-status locked">
                              <h4>Stage 2: Contribution Review</h4>
                              <p>🔒 Complete Stage 1 first</p>
                            </div>
                          ) : null}

                          {/* Stage 3: Ethical Alignment */}
                          {myTrial.stage_statuses?.stage_3 === 'in_progress' || myTrial.stage_statuses?.stage_3 === 'revision_requested' ? (
                            <div className="trial-stage-form">
                              <h4>Stage 3: Ethical Alignment</h4>
                              {myTrial.stage_statuses?.stage_3 === 'revision_requested' && (
                                <div className="revision-notice">
                                  <strong>Revision Requested:</strong> {myTrial.review_notes?.stage_3}
                                </div>
                              )}
                              <p className="stage-instruction">Answer each scenario question thoughtfully. Reason from the Charter, not from ego, fear, or convenience.</p>
                              
                              {trialContent.stage_3_scenarios?.map((scenario, i) => (
                                <div key={i} className="scenario-field">
                                  <label>Scenario {i + 1}: {scenario}</label>
                                  <textarea
                                    value={stage3Answers[`scenario_${i + 1}`] || ''}
                                    onChange={(e) => setStage3Answers({
                                      ...stage3Answers,
                                      [`scenario_${i + 1}`]: e.target.value
                                    })}
                                    rows={4}
                                    placeholder="Your reasoned response..."
                                  />
                                </div>
                              ))}
                              
                              <button 
                                className="trial-submit-btn"
                                onClick={submitStage3}
                                disabled={trialLoading || !Object.values(stage3Answers).every(v => v.trim())}
                              >
                                {trialLoading ? 'Submitting...' : 'Submit Stage 3'}
                              </button>
                            </div>
                          ) : myTrial.stage_statuses?.stage_3 === 'submitted' ? (
                            <div className="trial-stage-status">
                              <h4>Stage 3: Ethical Alignment</h4>
                              <p className="awaiting">Awaiting review...</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_3 === 'approved' && myTrial.current_stage > 3 ? (
                            <div className="trial-stage-status approved">
                              <h4>Stage 3: Ethical Alignment ✓</h4>
                              <p>Approved</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_3 === 'locked' ? (
                            <div className="trial-stage-status locked">
                              <h4>Stage 3: Ethical Alignment</h4>
                              <p>🔒 Complete earlier stages first</p>
                            </div>
                          ) : null}

                          {/* Stage 4: Peer Endorsement */}
                          {myTrial.stage_statuses?.stage_4 === 'in_progress' || myTrial.stage_statuses?.stage_4 === 'revision_requested' ? (
                            <div className="trial-stage-form">
                              <h4>Stage 4: Peer Endorsement</h4>
                              {myTrial.stage_statuses?.stage_4 === 'revision_requested' && (
                                <div className="revision-notice">
                                  <strong>Revision Requested:</strong> {myTrial.review_notes?.stage_4}
                                </div>
                              )}
                              <p className="stage-instruction">{trialContent.stage_4_description}</p>
                              
                              {myTrial.stage_4_endorsers?.length > 0 ? (
                                <div className="endorsement-status">
                                  <h5>Awaiting endorsements from:</h5>
                                  {myTrial.stage_4_endorsements?.map((e, i) => (
                                    <div key={i} className={`endorser-status ${e.submitted ? 'received' : 'pending'}`}>
                                      <span>{e.endorser_username}</span>
                                      <span>{e.submitted ? '✓ Submitted' : '⏳ Pending'}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="endorser-selection">
                                  <p>Select at least 2 members to endorse your readiness:</p>
                                  <select 
                                    multiple 
                                    value={stage4SelectedEndorsers}
                                    onChange={(e) => {
                                      const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                      setStage4SelectedEndorsers(selected);
                                    }}
                                    className="endorser-select"
                                  >
                                    {[...onlineMembers, ...offlineMembers].filter(m => m.id !== user?._id).map(m => (
                                      <option key={m.id} value={m.id}>{m.username} ({m.role})</option>
                                    ))}
                                  </select>
                                  <p className="selection-hint">Hold Ctrl/Cmd to select multiple</p>
                                  <button 
                                    className="trial-submit-btn"
                                    onClick={selectEndorsers}
                                    disabled={trialLoading || stage4SelectedEndorsers.length < 2}
                                  >
                                    {trialLoading ? 'Sending...' : `Send Endorsement Requests (${stage4SelectedEndorsers.length} selected)`}
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : myTrial.stage_statuses?.stage_4 === 'submitted' ? (
                            <div className="trial-stage-status">
                              <h4>Stage 4: Peer Endorsement</h4>
                              <p className="awaiting">All endorsements received. Awaiting review...</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_4 === 'approved' && myTrial.current_stage > 4 ? (
                            <div className="trial-stage-status approved">
                              <h4>Stage 4: Peer Endorsement ✓</h4>
                              <p>Approved</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_4 === 'locked' ? (
                            <div className="trial-stage-status locked">
                              <h4>Stage 4: Peer Endorsement</h4>
                              <p>🔒 Complete earlier stages first</p>
                            </div>
                          ) : null}

                          {/* Stage 5: Steward Affirmation */}
                          {myTrial.stage_statuses?.stage_5 === 'in_progress' ? (
                            <div className="trial-stage-form">
                              <h4>Stage 5: Steward Affirmation</h4>
                              <p className="stage-instruction">{trialContent.stage_5_description}</p>
                              
                              {!myTrial.stage_5_choice ? (
                                <div className="affirmation-choice">
                                  <p>How would you like to receive your affirmation?</p>
                                  <div className="choice-buttons">
                                    <button 
                                      className="choice-btn live"
                                      onClick={() => chooseStage5Method('live_meeting')}
                                      disabled={trialLoading}
                                    >
                                      Live Meeting Chat
                                    </button>
                                    <button 
                                      className="choice-btn written"
                                      onClick={() => chooseStage5Method('written_feedback')}
                                      disabled={trialLoading}
                                    >
                                      Written Feedback
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="affirmation-waiting">
                                  <p>You have chosen: <strong>{myTrial.stage_5_choice === 'live_meeting' ? 'Live Meeting' : 'Written Feedback'}</strong></p>
                                  <p className="awaiting">Awaiting reviewer to complete your trial...</p>
                                </div>
                              )}
                            </div>
                          ) : myTrial.stage_statuses?.stage_5 === 'completed' ? (
                            <div className="trial-stage-status approved">
                              <h4>Stage 5: Steward Affirmation ✓</h4>
                              <p>Completed</p>
                            </div>
                          ) : myTrial.stage_statuses?.stage_5 === 'locked' ? (
                            <div className="trial-stage-status locked">
                              <h4>Stage 5: Steward Affirmation</h4>
                              <p>🔒 Complete earlier stages first</p>
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Pending Endorsements (for others) */}
                      {pendingEndorsements.length > 0 && (
                        <div className="pending-endorsements-section">
                          <h4>Endorsement Requests</h4>
                          <p>You have been asked to endorse the following candidates:</p>
                          {pendingEndorsements.map(pe => (
                            <div key={pe.trial_id} className="endorsement-request-card">
                              <div className="endorsement-header">
                                <strong>{pe.candidate_username}</strong> is seeking promotion to <strong>{pe.target_role}</strong>
                              </div>
                              <div className="endorsement-form">
                                <textarea
                                  placeholder="Provide your endorsement or reason why you cannot endorse this candidate..."
                                  value={endorsementProofText}
                                  onChange={(e) => setEndorsementProofText(e.target.value)}
                                  rows={4}
                                />
                                <button 
                                  className="endorsement-submit-btn"
                                  onClick={() => submitEndorsement(pe.trial_id)}
                                  disabled={trialLoading || !endorsementProofText.trim()}
                                >
                                  {trialLoading ? 'Submitting...' : 'Submit Endorsement'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Trial Reviews Tab (Steward+ only) */}
                  {journeyTab === 'trial-reviews' && ['Steward', 'Archivist', 'Aurel', 'Founder'].includes(user?.role) && trialContent && (
                    <div className="trial-reviews-tab">
                      {trialSuccess && <div className="trial-success">{trialSuccess}</div>}
                      {trialError && <div className="trial-error">{trialError}</div>}
                      
                      {/* Reviewer Guidelines */}
                      <div className="reviewer-guidelines">
                        <details>
                          <summary>📋 Review Guidelines (Click to expand)</summary>
                          <div className="guidelines-content">
                            <div className="guideline-section">
                              <h5>Standards by Tier</h5>
                              <ul>
                                {trialContent.standards_by_tier?.map((s, i) => (
                                  <li key={i}><strong>{s.tier}:</strong> {s.standard}</li>
                                ))}
                              </ul>
                            </div>
                            <div className="guideline-section">
                              <h5>Grounds for Delay or Refusal</h5>
                              <ul>
                                {trialContent.grounds_for_delay_or_refusal?.map((g, i) => (
                                  <li key={i}>{g}</li>
                                ))}
                              </ul>
                            </div>
                            <div className="guideline-section">
                              <h5>Anti-Corruption Rule</h5>
                              <p>{trialContent.anti_corruption_rule}</p>
                            </div>
                            <div className="guideline-section">
                              <h5>Closing Position</h5>
                              <p>{trialContent.closing_position}</p>
                            </div>
                          </div>
                        </details>
                      </div>

                      {/* Review Tabs */}
                      <div className="review-sub-tabs">
                        <button 
                          className={trialReviewTab === 'active' ? 'active' : ''}
                          onClick={() => setTrialReviewTab('active')}
                        >
                          Active Trials ({trialReviews.active?.length || 0})
                        </button>
                        <button 
                          className={trialReviewTab === 'passed' ? 'active' : ''}
                          onClick={() => setTrialReviewTab('passed')}
                        >
                          Passed ({trialReviews.passed?.length || 0})
                        </button>
                        <button 
                          className={trialReviewTab === 'failed' ? 'active' : ''}
                          onClick={() => setTrialReviewTab('failed')}
                        >
                          Failed ({trialReviews.failed?.length || 0})
                        </button>
                      </div>

                      {/* Trial Review List */}
                      <div className="trial-review-list">
                        {trialReviewTab === 'active' && (
                          trialReviews.active?.length === 0 ? (
                            <p className="no-trials">No active trials to review.</p>
                          ) : (
                            trialReviews.active?.map(trial => (
                              <div key={trial.trial_id} className="trial-review-card">
                                <div className="trial-review-header">
                                  <strong>{trial.username}</strong>
                                  <span className="trial-path">{trial.current_role} → {trial.target_role}</span>
                                  <span className="trial-stage">Stage {trial.current_stage}/5</span>
                                </div>
                                
                                {/* Show stage that needs review */}
                                {[1, 2, 3, 4].map(stageNum => {
                                  const stageKey = `stage_${stageNum}`;
                                  if (trial.stage_statuses?.[stageKey] !== 'submitted') return null;
                                  
                                  return (
                                    <div key={stageNum} className="stage-review-section">
                                      <h5>Stage {stageNum} - Ready for Review</h5>
                                      
                                      {stageNum === 1 && trial.stage_1_reflections && (
                                        <div className="reflection-review">
                                          {Object.entries(trial.stage_1_reflections).map(([key, value]) => (
                                            <div key={key} className="reflection-item">
                                              <strong>{key.replace(/_/g, ' ')}:</strong>
                                              <p>{value}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {stageNum === 2 && (
                                        <div className="contribution-review">
                                          <p><strong>Description:</strong> {trial.stage_2_description}</p>
                                          {trial.stage_2_files?.length > 0 && (
                                            <div className="files-list">
                                              <strong>Files:</strong>
                                              {trial.stage_2_files.map((f, i) => (
                                                <span key={i} className="file-chip">{f.filename}</span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {stageNum === 3 && trial.stage_3_answers && (
                                        <div className="scenario-review">
                                          {Object.entries(trial.stage_3_answers).map(([key, value]) => (
                                            <div key={key} className="scenario-item">
                                              <strong>Scenario {key.split('_')[1]}:</strong>
                                              <p>{value}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {stageNum === 4 && trial.stage_4_endorsements && (
                                        <div className="endorsement-review">
                                          {trial.stage_4_endorsements.map((e, i) => (
                                            <div key={i} className="endorsement-item">
                                              <strong>{e.endorser_username}:</strong>
                                              <p>{e.proof_text || '(File uploaded)'}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      
                                      <div className="review-action-form">
                                        <textarea
                                          placeholder="Review notes (optional for approval, required for revision request)..."
                                          value={reviewNotes}
                                          onChange={(e) => setReviewNotes(e.target.value)}
                                          rows={2}
                                        />
                                        <div className="review-buttons">
                                          <button 
                                            className="approve-btn"
                                            onClick={() => reviewTrialStage(trial.trial_id, stageNum, true)}
                                            disabled={trialLoading}
                                          >
                                            ✓ Approve Stage {stageNum}
                                          </button>
                                          <button 
                                            className="revision-btn"
                                            onClick={() => reviewTrialStage(trial.trial_id, stageNum, false)}
                                            disabled={trialLoading || !reviewNotes.trim()}
                                          >
                                            ↺ Request Revision
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                
                                {/* Stage 5 completion (if all previous approved) */}
                                {trial.stage_statuses?.stage_4 === 'approved' && trial.stage_statuses?.stage_5 === 'in_progress' && trial.stage_5_choice && (
                                  <div className="stage-5-completion">
                                    <h5>Stage 5 - Final Affirmation</h5>
                                    <p>Candidate chose: <strong>{trial.stage_5_choice === 'live_meeting' ? 'Live Meeting' : 'Written Feedback'}</strong></p>
                                    
                                    <div className="final-decision-form">
                                      <label>Outcome:</label>
                                      <select 
                                        value={trialFinalOutcome}
                                        onChange={(e) => setTrialFinalOutcome(e.target.value)}
                                      >
                                        <option value="">Select outcome...</option>
                                        <option value="promoted">Promote to {trial.target_role}</option>
                                        <option value="guidance">Provide Guidance (Not Yet Ready)</option>
                                      </select>
                                      
                                      <label>Feedback:</label>
                                      <textarea
                                        value={trialFinalFeedback}
                                        onChange={(e) => setTrialFinalFeedback(e.target.value)}
                                        rows={4}
                                        placeholder={trialFinalOutcome === 'promoted' 
                                          ? 'Congratulations message and any guidance for their new role...'
                                          : 'What is missing, what should be strengthened, and what conduct would demonstrate readiness...'
                                        }
                                      />
                                      
                                      <button 
                                        className="complete-trial-btn"
                                        onClick={() => completeTrial(trial.trial_id)}
                                        disabled={trialLoading || !trialFinalOutcome || !trialFinalFeedback.trim()}
                                      >
                                        {trialLoading ? 'Processing...' : 'Complete Trial'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))
                          )
                        )}
                        
                        {trialReviewTab === 'passed' && (
                          trialReviews.passed?.length === 0 ? (
                            <p className="no-trials">No passed trials yet.</p>
                          ) : (
                            trialReviews.passed?.map(trial => (
                              <div key={trial.trial_id} className="trial-review-card completed passed">
                                <div className="trial-review-header">
                                  <strong>{trial.username}</strong>
                                  <span className="trial-path">{trial.current_role} → {trial.target_role}</span>
                                  <span className="trial-outcome">✓ Promoted</span>
                                </div>
                                <p className="trial-date">Completed: {new Date(trial.completed_at).toLocaleDateString()}</p>
                              </div>
                            ))
                          )
                        )}
                        
                        {trialReviewTab === 'failed' && (
                          trialReviews.failed?.length === 0 ? (
                            <p className="no-trials">No failed trials yet.</p>
                          ) : (
                            trialReviews.failed?.map(trial => (
                              <div key={trial.trial_id} className="trial-review-card completed failed">
                                <div className="trial-review-header">
                                  <strong>{trial.username}</strong>
                                  <span className="trial-path">{trial.current_role} → {trial.target_role}</span>
                                  <span className="trial-outcome">Guidance Provided</span>
                                </div>
                                <p className="trial-date">Completed: {new Date(trial.completed_at).toLocaleDateString()}</p>
                                <details>
                                  <summary>View Feedback</summary>
                                  <p className="trial-feedback">{trial.stage_5_feedback}</p>
                                </details>
                              </div>
                            ))
                          )
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Journey Submit Modal */}
      {showJourneySubmitModal && selectedJourneyTask && (
        <div className="modal-overlay" onClick={() => setShowJourneySubmitModal(false)}>
          <div className="journey-submit-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowJourneySubmitModal(false)}>×</button>
            
            <h3>Submit Proof</h3>
            <div className="selected-task">
              <h4>{selectedJourneyTask.title}</h4>
              <p>{selectedJourneyTask.description}</p>
              {selectedJourneyTask.witnesses_required > 0 && (
                <div className="witness-requirement">
                  ⚠️ This task requires {selectedJourneyTask.witnesses_required} witness{selectedJourneyTask.witnesses_required > 1 ? 'es' : ''} to confirm.
                </div>
              )}
            </div>

            <div className="submit-form">
              <label>
                Describe your proof:
                <textarea
                  value={journeyProofDescription}
                  onChange={(e) => setJourneyProofDescription(e.target.value)}
                  placeholder="Explain what you did and how it fulfills this task..."
                  rows={4}
                />
              </label>

              <label>
                Upload evidence (optional):
                <input
                  type="file"
                  onChange={(e) => setJourneyProofFile(e.target.files[0])}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                />
                {journeyProofFile && <span className="file-name">{journeyProofFile.name}</span>}
              </label>

              {selectedJourneyTask.witnesses_required > 0 && (
                <div className="witness-selection">
                  <label>Tag witnesses ({selectedJourneyTask.witnesses_required} required):</label>
                  <select
                    onChange={(e) => {
                      if (e.target.value && !journeyWitnesses.includes(e.target.value)) {
                        if (journeyWitnesses.length < selectedJourneyTask.witnesses_required) {
                          setJourneyWitnesses([...journeyWitnesses, e.target.value]);
                        }
                      }
                      e.target.value = '';
                    }}
                  >
                    <option value="">Select a member...</option>
                    {allUsers.filter(u => u.id !== user?.id && !journeyWitnesses.includes(u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.username}</option>
                    ))}
                  </select>
                  <div className="selected-witnesses">
                    {journeyWitnesses.map(wid => {
                      const witnessUser = allUsers.find(u => u.id === wid);
                      return (
                        <span key={wid} className="witness-tag">
                          {witnessUser?.username || wid}
                          <button onClick={() => setJourneyWitnesses(journeyWitnesses.filter(w => w !== wid))}>×</button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {journeyError && <div className="form-error">{journeyError}</div>}

              <div className="submit-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowJourneySubmitModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="submit-btn"
                  onClick={submitJourneyProof}
                  disabled={journeySubmitting || !journeyProofDescription.trim()}
                >
                  {journeySubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Journey Gift Modal (Steward+) */}
      {showJourneyGiftModal && (
        <div className="modal-overlay" onClick={() => setShowJourneyGiftModal(false)}>
          <div className="journey-gift-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowJourneyGiftModal(false)}>×</button>
            
            <h3>🎁 Award Task Completion</h3>
            <p className="gift-description">Directly award a journey task completion to a member.</p>

            <div className="gift-form">
              <label>
                Select Member:
                <select value={journeyGiftUser} onChange={(e) => setJourneyGiftUser(e.target.value)}>
                  <option value="">Choose a member...</option>
                  {allUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                  ))}
                </select>
              </label>

              <label>
                Select Task:
                <select value={journeyGiftTask} onChange={(e) => setJourneyGiftTask(e.target.value)}>
                  <option value="">Choose a task...</option>
                  {journeyTasks.map(t => (
                    <option key={t.id} value={t.id}>{t.title} ({t.room})</option>
                  ))}
                </select>
              </label>

              <label>
                Notes (optional):
                <textarea
                  value={journeyGiftNotes}
                  onChange={(e) => setJourneyGiftNotes(e.target.value)}
                  placeholder="Reason for awarding..."
                  rows={2}
                />
              </label>

              {journeyError && <div className="form-error">{journeyError}</div>}

              <div className="gift-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowJourneyGiftModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="gift-confirm-btn"
                  onClick={giftJourneyCompletion}
                  disabled={!journeyGiftUser || !journeyGiftTask}
                >
                  Award
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Access Denied Popup */}
      {accessDeniedPopup && (
        <div className="access-denied-overlay" onClick={() => setAccessDeniedPopup(null)}>
          <div className="access-denied-popup" onClick={(e) => e.stopPropagation()} data-testid="access-denied-popup">
            <button className="popup-close" onClick={() => setAccessDeniedPopup(null)}>×</button>
            
            <div className="denied-icon">🔒</div>
            <h3 className="denied-title">Access Restricted</h3>
            <p className="denied-room">{accessDeniedPopup.roomName}</p>
            
            <div className="denied-requirements">
              {accessDeniedPopup.minRole && (
                <div className="requirement-item">
                  <span className="req-icon">👤</span>
                  <span className="req-text">
                    <strong>{accessDeniedPopup.minRole}</strong> role or above required
                  </span>
                </div>
              )}
              {accessDeniedPopup.arcoRequired > 0 && (
                <div className="requirement-item">
                  <span className="req-icon">🌳</span>
                  <span className="req-text">
                    <strong>{accessDeniedPopup.arcoRequired.toLocaleString()}</strong> ARCO minimum holding
                  </span>
                </div>
              )}
            </div>
            
            <div className="denied-current">
              <span>Your current status:</span>
              <div className="current-stats">
                <span className="current-role">{user?.role || 'Member'}</span>
                <span className="current-arco">{arcoBalance?.toLocaleString() || 0} ARCO</span>
              </div>
            </div>
            
            <button className="denied-dismiss" onClick={() => setAccessDeniedPopup(null)}>
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuildHall;
